import React, { useState, useEffect, useRef } from "react";
import Cosmos from "./Cosmos";
import {
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// ── SVG icon components (line art, no fill) ──────────────────────────────────
const IconSun = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="3.4" stroke={color} strokeWidth="1.35" />
    <line
      x1="10"
      y1="1.5"
      x2="10"
      y2="4.2"
      stroke={color}
      strokeWidth="1.35"
      strokeLinecap="round"
    />
    <line
      x1="10"
      y1="15.8"
      x2="10"
      y2="18.5"
      stroke={color}
      strokeWidth="1.35"
      strokeLinecap="round"
    />
    <line
      x1="1.5"
      y1="10"
      x2="4.2"
      y2="10"
      stroke={color}
      strokeWidth="1.35"
      strokeLinecap="round"
    />
    <line
      x1="15.8"
      y1="10"
      x2="18.5"
      y2="10"
      stroke={color}
      strokeWidth="1.35"
      strokeLinecap="round"
    />
    <line
      x1="3.9"
      y1="3.9"
      x2="5.7"
      y2="5.7"
      stroke={color}
      strokeWidth="1.35"
      strokeLinecap="round"
    />
    <line
      x1="14.3"
      y1="14.3"
      x2="16.1"
      y2="16.1"
      stroke={color}
      strokeWidth="1.35"
      strokeLinecap="round"
    />
    <line
      x1="3.9"
      y1="16.1"
      x2="5.7"
      y2="14.3"
      stroke={color}
      strokeWidth="1.35"
      strokeLinecap="round"
    />
    <line
      x1="14.3"
      y1="5.7"
      x2="16.1"
      y2="3.9"
      stroke={color}
      strokeWidth="1.35"
      strokeLinecap="round"
    />
  </svg>
);

const IconSparkle = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
    <path
      d="M10 3L11.3 8.7L17 10L11.3 11.3L10 17L8.7 11.3L3 10L8.7 8.7Z"
      stroke={color}
      strokeWidth="1.25"
      strokeLinejoin="round"
    />
  </svg>
);

const IconChart = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
    <polyline
      points="2.5,15.5 6.5,10.5 10.5,12.5 17.5,5"
      stroke={color}
      strokeWidth="1.35"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line
      x1="2.5"
      y1="15.5"
      x2="17.5"
      y2="15.5"
      stroke={color}
      strokeWidth="1.1"
      strokeLinecap="round"
      strokeOpacity="0.5"
    />
  </svg>
);

const IconCalendar = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
    <rect
      x="2.5"
      y="4"
      width="15"
      height="13.5"
      rx="2.5"
      stroke={color}
      strokeWidth="1.25"
    />
    <line
      x1="2.5"
      y1="8.5"
      x2="17.5"
      y2="8.5"
      stroke={color}
      strokeWidth="1.1"
    />
    <line
      x1="7"
      y1="2.5"
      x2="7"
      y2="5.5"
      stroke={color}
      strokeWidth="1.35"
      strokeLinecap="round"
    />
    <line
      x1="13"
      y1="2.5"
      x2="13"
      y2="5.5"
      stroke={color}
      strokeWidth="1.35"
      strokeLinecap="round"
    />
  </svg>
);

const IconInfinity = ({ color }) => (
  <svg width="24" height="22" viewBox="0 0 26 20" fill="none">
    <path
      d="M13 10 C13 10 10 5 6.5 5 C3.5 5 1 7.5 1 10 C1 12.5 3.5 15 6.5 15 C10 15 13 10 13 10 C13 10 16 5 19.5 5 C22.5 5 25 7.5 25 10 C25 12.5 22.5 15 19.5 15 C16 15 13 10 13 10 Z"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconHeart = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
    <path
      d="M10 15.5C10 15.5 3 11 3 6.8C3 5.1 4.5 3.8 6.3 3.8C7.7 3.8 8.9 4.6 10 5.7C11.1 4.6 12.3 3.8 13.7 3.8C15.5 3.8 17 5.1 17 6.8C17 11 10 15.5 10 15.5Z"
      stroke={color}
      strokeWidth="1.35"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ── Glass panel shared style ──────────────────────────────────────────────────
const panel = {
  background:
    "linear-gradient(160deg,rgba(255, 255, 255, 0.15),rgba(255, 255, 255, 0.06) 60%,rgba(255, 255, 255, 0.1))",
  backdropFilter: "blur(24px) saturate(140%)",
  WebkitBackdropFilter: "blur(24px) saturate(140%)",
  borderRadius: "30px",
  boxShadow:
    "0 26px 50px -28px rgba(8, 4, 40, 0.7), inset 1px 1px 0 rgba(255, 255, 255, 0.5), inset -1px -1px 0 rgba(255, 255, 255, 0.12), inset 0 0 0 1px rgba(255, 255, 255, 0.08)",
  padding: "20px",
  border: "none",
};

export default function App() {
  const [activeTab, setActiveTab] = useState("home");
  const [apiKey, setApiKey] = useState("");
  const [showApiInput, setShowApiInput] = useState(true);
  const [inspiringQuote, setInspiringQuote] = useState("");
  const [isEditingQuote, setIsEditingQuote] = useState(false);
  const [createQuote, setCreateQuote] = useState("");
  const [isEditingCreateQuote, setIsEditingCreateQuote] = useState(false);
  const [coverImage, setCoverImage] = useState("");
  // Glass panel style; a touch more opaque over a cover photo so text stays readable.
  const pnl = coverImage
    ? {
        ...panel,
        background:
          "linear-gradient(160deg,rgba(66, 46, 160, 0.58),rgba(44, 30, 116, 0.46))",
        backdropFilter: "blur(28px) saturate(140%)",
        WebkitBackdropFilter: "blur(28px) saturate(140%)",
      }
    : panel;
  const [isEditingCover, setIsEditingCover] = useState(false);
  const [manifestationMultiplier, setManifestationMultiplier] = useState("");

  const [cryptoHoldings, setCryptoHoldings] = useState({
    USDC: 0,
    SGD: 0,
    ETH: 0,
    SOL: 0,
    BNB: 0,
  });
  const [fiatHoldings, setFiatHoldings] = useState({
    cash: 0,
    primeXBT: 0,
    posbSpending: 0,
    ocbc: 0,
  });
  const [pendingCryptoChanges, setPendingCryptoChanges] = useState(false);
  const [pendingFiatChanges, setPendingFiatChanges] = useState(false);
  const [prices, setPrices] = useState({ USDC: 0, ETH: 0, SOL: 0, BNB: 0 });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState("month");
  const [historyView, setHistoryView] = useState("month");
  const [calendarView, setCalendarView] = useState(new Date());
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);

  // ── Create tab ────────────────────────────────────────────────────────────
  const [revenueStreams, setRevenueStreams] = useState([]);
  const [showAddStream, setShowAddStream] = useState(false);
  const [editingStreamId, setEditingStreamId] = useState(null);
  const [projectionYears, setProjectionYears] = useState(5);
  const [orbMonth, setOrbMonth] = useState(36);
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 640
  );
  const blankStream = {
    name: "",
    amount: "",
    amountUnit: "mo",
    startDate: "",
    duration: "",
    durationUnit: "mo",
    type: "recurring",
    compound: "",
  };
  const [streamForm, setStreamForm] = useState(blankStream);
  const chartRef = useRef(null);
  const merkabaCRef = useRef(null);
  const infinitySpinRef = useRef(35);
  const whisperInputRef = useRef(null);
  const [infinityQuote, setInfinityQuote] = useState(
    "Create from the infinite field."
  );
  const [isEditingInfinityQuote, setIsEditingInfinityQuote] = useState(false);
  const [infinitySpinSpeed, setInfinitySpinSpeed] = useState(35);

  // ── Heart / Give tab ────────────────────────────────────────────────────
  const [actsOfLove, setActsOfLove] = useState([]);
  const [loveForm, setLoveForm] = useState({ action: "", amount: "" });
  const [heartQuote, setHeartQuote] = useState(
    "The love you give is the love you receive."
  );
  const [isEditingHeartQuote, setIsEditingHeartQuote] = useState(false);
  const [flowerConverging, setFlowerConverging] = useState(false);
  const [whisperText, setWhisperText] = useState("");
  const [whisperGlowing, setWhisperGlowing] = useState(false);

  // Inject global styles + keyframes
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');
      * { font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif !important; box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
      html, body { overflow-x: hidden; width: 100%; margin: 0; padding: 0; }
      @keyframes creationGlow {
        0%, 100% { text-shadow: 0 0 14px rgba(255, 255, 255, 0.75), 0 0 42px rgba(176, 146, 255, 0.65), 0 0 90px rgba(224, 120, 205, 0.4); }
        50%      { text-shadow: 0 0 18px rgba(255, 255, 255, 0.95), 0 0 52px rgba(176, 146, 255, 0.8), 0 0 100px rgba(224, 120, 205, 0.5); }
      }
      .creation-title { animation: creationGlow 3s ease-in-out infinite; opacity: 1; }
      input:not(.manifest-slider):not(.infinity-slider):not(.whisper-field):focus, textarea:not(.whisper-field):focus { outline: none !important; box-shadow: 0 0 6px rgba(255,255,255,0.42) !important; border-color: rgba(255,255,255,0.85) !important; }
      ::placeholder { color: rgba(208,198,244,0.45) !important; }
      @keyframes refreshSpin { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
      @keyframes iconGlowPulse {
        0%,100% { filter: drop-shadow(0 0 4px rgba(255,255,255,0.7)) drop-shadow(0 0 10px rgba(236, 228, 255, 0.4)); }
        50%      { filter: drop-shadow(0 0 7px rgba(255,255,255,0.95)) drop-shadow(0 0 18px rgba(236, 228, 255, 0.65)); }
      }
      .sun-icon { animation: iconGlowPulse 3.5s ease-in-out infinite; }
      @keyframes refreshSpin { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
      @keyframes sunPulse {
        0%   { filter: drop-shadow(0 0 4px rgba(255,255,255,0.6)) drop-shadow(0 0 10px rgba(236, 228, 255, 0.4)); }
        50%  { filter: drop-shadow(0 0 8px rgba(255,255,255,0.95)) drop-shadow(0 0 20px rgba(236, 228, 255, 0.65)) drop-shadow(0 0 36px rgba(196, 176, 255, 0.35)); }
        100% { filter: drop-shadow(0 0 4px rgba(255,255,255,0.6)) drop-shadow(0 0 10px rgba(236, 228, 255, 0.4)); }
      }
      .sun-icon { animation: sunPulse 4s ease-in-out infinite; }
      .refresh-btn:active svg { transform: rotate(-180deg) scale(0.9); }
      .refresh-btn svg { transition: transform 0.4s cubic-bezier(0.34,1.56,0.64,1); }
      @keyframes thumbPulse {
        0%   { box-shadow: 0 0 8px 4px rgba(255,255,255,0.7),  0 0 20px 8px rgba(236, 228, 255, 0.4),  0 0 40px 12px rgba(196, 176, 255, 0.2); }
        50%  { box-shadow: 0 0 12px 6px rgba(255,255,255,0.9), 0 0 28px 12px rgba(236, 228, 255, 0.55), 0 0 55px 16px rgba(196, 176, 255, 0.3); }
        100% { box-shadow: 0 0 8px 4px rgba(255,255,255,0.7),  0 0 20px 8px rgba(236, 228, 255, 0.4),  0 0 40px 12px rgba(196, 176, 255, 0.2); }
      }
      .manifest-slider { -webkit-appearance: none; appearance: none; outline: none; background: transparent; cursor: pointer; width: 100%; height: 26px; accent-color: transparent; }
      .manifest-slider::-webkit-slider-runnable-track { height: 1.5px; background: rgba(255,255,255,0.65); border-radius: 1px; box-shadow: 0 0 4px 1px rgba(255,255,255,0.7), 0 0 10px 2px rgba(236, 228, 255, 0.45), 0 0 20px 3px rgba(196, 176, 255, 0.25); }
      .manifest-slider::-moz-range-track { height: 1.5px; background: rgba(255,255,255,0.65); border-radius: 1px; box-shadow: 0 0 4px 1px rgba(255,255,255,0.7), 0 0 10px 2px rgba(236, 228, 255, 0.45); }
      .manifest-slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 16px; height: 16px; border-radius: 50%; background: rgba(255, 255, 255, 0.92); border: none; margin-top: -7px; box-shadow: 0 0 10px 6px rgba(255,255,255,0.6), 0 0 22px 10px rgba(236, 228, 255, 0.35), 0 0 40px 14px rgba(196, 176, 255, 0.18); animation: thumbPulse 3s ease-in-out infinite; }
      .manifest-slider::-moz-range-thumb { width: 16px; height: 16px; border-radius: 50%; background: rgba(255, 255, 255, 0.92); border: none; box-shadow: 0 0 10px 6px rgba(255,255,255,0.6), 0 0 22px 10px rgba(236, 228, 255, 0.35); }
      .manifest-slider::-webkit-slider-thumb:hover { animation: none; background: rgba(255,255,255,0.97); box-shadow: 0 0 14px 7px rgba(255,255,255,0.95), 0 0 34px 14px rgba(236, 228, 255, 0.65), 0 0 60px 18px rgba(196, 176, 255, 0.38); transform: scale(1.15); }
      .infinity-slider { -webkit-appearance: none; appearance: none; outline: none; background: transparent; cursor: pointer; width: 100%; height: 26px; accent-color: transparent; border: none; }
      .infinity-slider:focus { outline: none; box-shadow: none; border: none; }
      .infinity-slider:active { outline: none; box-shadow: none; border: none; }
      .infinity-slider::-webkit-slider-runnable-track { height: 1.5px; background: rgba(255,255,255,0.65); border-radius: 1px; box-shadow: 0 0 4px 1px rgba(255,255,255,0.7), 0 0 10px 2px rgba(236, 228, 255, 0.45), 0 0 20px 3px rgba(196, 176, 255, 0.25); }
      .infinity-slider::-moz-range-track { height: 1.5px; background: rgba(255,255,255,0.65); border-radius: 1px; box-shadow: 0 0 4px 1px rgba(255,255,255,0.7), 0 0 10px 2px rgba(236, 228, 255, 0.45); }
      .infinity-slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 16px; height: 16px; border-radius: 50%; background: rgba(255, 255, 255, 0.92); border: none; margin-top: -7px; box-shadow: 0 0 10px 6px rgba(255,255,255,0.6), 0 0 22px 10px rgba(236, 228, 255, 0.35), 0 0 40px 14px rgba(196, 176, 255, 0.18); animation: thumbPulse 3s ease-in-out infinite; }
      .infinity-slider::-moz-range-thumb { width: 16px; height: 16px; border-radius: 50%; background: rgba(255, 255, 255, 0.92); border: none; box-shadow: 0 0 10px 6px rgba(255,255,255,0.6), 0 0 22px 10px rgba(236, 228, 255, 0.35); }
      .infinity-slider::-webkit-slider-thumb:hover { animation: none; background: rgba(255,255,255,0.97); box-shadow: 0 0 14px 7px rgba(255,255,255,0.95), 0 0 34px 14px rgba(236, 228, 255, 0.65), 0 0 60px 18px rgba(196, 176, 255, 0.38); transform: scale(1.15); }
      @keyframes metatronReveal {
        0%   { -webkit-mask-size: 15% 15%; mask-size: 15% 15%; }
        30%  { -webkit-mask-size: 250% 250%; mask-size: 250% 250%; }
        70%  { -webkit-mask-size: 250% 250%; mask-size: 250% 250%; }
        100% { -webkit-mask-size: 15% 15%; mask-size: 15% 15%; }
      }
      .metatron-pulse {
        -webkit-mask-image: radial-gradient(circle, white 30%, transparent 60%);
        mask-image: radial-gradient(circle, white 30%, transparent 60%);
        -webkit-mask-position: center;
        mask-position: center;
        -webkit-mask-repeat: no-repeat;
        mask-repeat: no-repeat;
        animation: metatronReveal 10s ease-in-out infinite;
      }
      @keyframes flowerRingReveal {
        0%   { opacity: 0; transform: scale(0.88); filter: blur(3px); }
        100% { opacity: 1; transform: scale(1); filter: blur(0px); }
      }
      .flower-ring-enter { animation: flowerRingReveal 1.2s ease-out forwards; opacity: 0; }
      @keyframes flowerFadeIn {
        0%   { opacity: 0; }
        100% { opacity: 1; }
      }
      @keyframes flowerFadeOut {
        0%   { opacity: 1; }
        100% { opacity: 0; }
      }
      @keyframes flowerConverge {
        0%   { opacity: 1; transform: scale(1); }
        60%  { opacity: 0.5; transform: scale(0.15); }
        100% { opacity: 0; transform: scale(0); }
      }
      .flower-converge { transform-origin: 200px 200px; animation: flowerConverge 2.8s ease-in forwards; }
      @keyframes flowerRadialPulse {
        0%   { r: 0; opacity: 0.3; }
        40%  { opacity: 0.18; }
        70%  { opacity: 0.08; }
        100% { r: 220; opacity: 0; }
      }
      .flower-radial-pulse { animation: flowerRadialPulse 4.5s ease-out infinite; }
      @keyframes flowerPulseGlow {
        0%,100% { filter: drop-shadow(0 0 6px rgba(255,255,255,0.4)) drop-shadow(0 0 14px rgba(255,255,255,0.2)); }
        50%     { filter: drop-shadow(0 0 10px rgba(255,255,255,0.65)) drop-shadow(0 0 22px rgba(255,255,255,0.35)); }
      }
      .flower-glow { animation: flowerPulseGlow 5s ease-in-out infinite; }
      @keyframes merkabaPulse {
        0%,100% { filter: drop-shadow(0 0 8px rgba(255,255,255,0.65)) drop-shadow(0 0 20px rgba(255,255,255,0.35)) drop-shadow(0 0 40px rgba(236, 228, 255, 0.18)); }
        50%     { filter: drop-shadow(0 0 14px rgba(255,255,255,0.9)) drop-shadow(0 0 32px rgba(255,255,255,0.55)) drop-shadow(0 0 60px rgba(236, 228, 255, 0.3)); }
      }
      .merkaba-canvas { animation: merkabaPulse 4s ease-in-out infinite; }
      @keyframes chartOrbPulse {
        0%,100% { filter: drop-shadow(0 0 3px rgba(255,255,255,1)) drop-shadow(0 0 8px rgba(255, 255, 255, 0.75)) drop-shadow(0 0 18px rgba(236, 228, 255, 0.4)) drop-shadow(0 0 32px rgba(196, 176, 255, 0.18)); }
        50%     { filter: drop-shadow(0 0 5px rgba(255,255,255,1)) drop-shadow(0 0 14px rgba(255, 255, 255, 1)) drop-shadow(0 0 28px rgba(236, 228, 255, 0.65)) drop-shadow(0 0 50px rgba(196, 176, 255, 0.32)); }
      }
      .chart-orb { animation: chartOrbPulse 3s ease-in-out infinite; }
      @keyframes whisperGlow {
        0%,100% { text-shadow: 0 0 8px rgba(255,255,255,0.7), 0 0 22px rgba(236, 228, 255, 0.45), 0 0 44px rgba(196, 176, 255, 0.22); }
        50%     { text-shadow: 0 0 14px rgba(255,255,255,1), 0 0 36px rgba(236, 228, 255, 0.85), 0 0 72px rgba(196, 176, 255, 0.5); }
      }
      @keyframes whisperOrbPulse {
        0%,100% { opacity: 0.18; transform: scale(1); }
        50%     { opacity: 0.38; transform: scale(1.12); }
      }
      .whisper-glow { animation: whisperGlow 2.5s ease-in-out infinite; font-weight: 400; color: rgba(255, 255, 255, 0.96) !important; }
      .whisper-orb { animation: whisperOrbPulse 2.5s ease-in-out infinite; }
      .whisper-field::placeholder { color: rgba(255, 255, 255, 0.28) !important; font-style: italic; }
      .whisper-field { caret-color: rgba(255,255,255,0.7); background: transparent !important; -webkit-appearance: none; box-shadow: none !important; border: none !important; font-size: 1.28rem !important; font-style: italic; font-family: inherit; }
      .whisper-field:focus { outline: none !important; box-shadow: none !important; border: none !important; }
      .chart-orb-dragging { animation: none !important; filter: drop-shadow(0 0 5px rgba(255,255,255,1)) drop-shadow(0 0 16px rgba(255, 255, 255, 1)) drop-shadow(0 0 34px rgba(236, 228, 255, 0.9)) drop-shadow(0 0 60px rgba(196, 176, 255, 0.55)) !important; }
      @media (max-width: 640px) {
        .grid-3 { grid-template-columns: 1fr !important; }
        .grid-2 { grid-template-columns: 1fr !important; }

      @media (min-width: 641px) {
        .grid-3 { grid-template-columns: repeat(3, 1fr) !important; }
        .grid-2 { grid-template-columns: repeat(2, 1fr) !important; }
      }
    `;
    document.head.appendChild(style);

    let viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      viewport = document.createElement("meta");
      viewport.name = "viewport";
      viewport.content =
        "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
      document.head.appendChild(viewport);
    }
    let themeColor = document.querySelector('meta[name="theme-color"]');
    if (!themeColor) {
      themeColor = document.createElement("meta");
      themeColor.name = "theme-color";
      themeColor.content = "#2b1a74";
      document.head.appendChild(themeColor);
    }
    return () => {
      try {
        document.head.removeChild(style);
      } catch (e) {}
    };
  }, []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // ── Infinity tab: sync spin speed ref ───────────────────────────────────
  useEffect(() => {
    infinitySpinRef.current = infinitySpinSpeed;
  }, [infinitySpinSpeed]);

  // ── Infinity tab: Three.js Merkaba (star tetrahedron) ─────────────────────
  useEffect(() => {
    if (activeTab !== "infinity") return;
    let cleanup = () => {};
    const canvas = merkabaCRef.current;
    if (!canvas) return;
    const initScene = () => {
      const T = window.THREE;
      if (!T) return;
      const w = canvas.offsetWidth || 300;
      const h = canvas.offsetHeight || 300;
      const renderer = new T.WebGLRenderer({
        canvas,
        alpha: true,
        antialias: true,
      });
      renderer.setSize(w, h);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      const scene = new T.Scene();
      // Orthographic camera for precise alignment with 2D Metatron SVG
      const frustum = 2.8;
      const camera = new T.OrthographicCamera(
        -frustum,
        frustum,
        frustum,
        -frustum,
        0.1,
        100
      );
      camera.position.set(0, 0, 10);
      camera.lookAt(0, 0, 0);

      scene.add(new T.AmbientLight(0xffffff, 0.5));
      // Directional light for glass specular highlights on tetrahedron faces
      const dirLight = new T.DirectionalLight(0xffffff, 0.8);
      dirLight.position.set(2, 3, 5);
      scene.add(dirLight);
      const dirLight2 = new T.DirectionalLight(0xffffff, 0.4);
      dirLight2.position.set(-2, -1, 3);
      scene.add(dirLight2);

      // ── Merkaba = star tetrahedron ──
      // Vertices anchored to the OUTER 6 nodes of the Metatron.
      // Metatron SVG: center=(200,200), outer ring r2=140, viewBox 400×400.
      // Outer nodes at angles (-90 + i*60)° for i=0..5:
      //   Node 7: top       (200, 60)   → Three.js (0, +frustum*0.7, 0)
      //   Node 8: top-right (321.2, 130) → Three.js (+f*0.606, +f*0.35, 0)
      //   Node 9: bot-right (321.2, 270) → Three.js (+f*0.606, -f*0.35, 0)
      //   Node 10: bottom   (200, 340)   → Three.js (0, -f*0.7, 0)
      //   Node 11: bot-left (78.8, 270)  → Three.js (-f*0.606, -f*0.35, 0)
      //   Node 12: top-left (78.8, 130)  → Three.js (-f*0.606, +f*0.35, 0)
      //
      // SVG to Three.js mapping: x_3d = (svgX - 200)/200 * frustum, y_3d = -(svgY - 200)/200 * frustum
      const f = frustum; // 2.8
      const s = 140 / 200; // 0.7 — ratio of outer ring to half-viewBox

      // The 6 outer node positions in Three.js XY coords
      const outerNodes3D = [0, 1, 2, 3, 4, 5].map((i) => {
        const a = ((-90 + i * 60) * Math.PI) / 180;
        return [f * s * Math.cos(a), f * s * -Math.sin(a)]; // negate sin for SVG-Y to Three.js-Y
      });
      // outerNodes3D[0] = top, [1] = top-right, [2] = bot-right, [3] = bottom, [4] = bot-left, [5] = top-left

      // From the annotated overlay:
      // GREEN (upward/male) tet: apex at node 0 (top), base at nodes 4 (bot-left), 2 (bot-right), + 3rd behind in Z
      // ORANGE (downward/female) tet: apex at node 3 (bottom), base at nodes 5 (top-left), 1 (top-right), + 3rd behind

      // For a regular tetrahedron: if apex is at (0, R2, 0) and base center at (0, baseY, 0),
      // the apex-to-base-vertex distance must equal the base edge length.
      // Front 2 base verts: nodes 4 (bot-left) and 2 (bot-right)
      const baseEdge = Math.sqrt(
        Math.pow(outerNodes3D[2][0] - outerNodes3D[4][0], 2) +
          Math.pow(outerNodes3D[2][1] - outerNodes3D[4][1], 2)
      );
      // For equilateral base triangle, the 3rd vertex is behind at the centroid Z-offset
      // But we need it to stay within visible bounds when spinning.
      // The perpendicular height of equilateral triangle = baseEdge * √3/2
      // However, since 2 verts are in XY plane (z=0), the 3rd goes into Z.
      // We use the actual equilateral math but the 3rd vertex position in the
      // base plane (which is in XZ when tet is upright).
      //
      // For a regular tet with the front base edge in the XY plane:
      // The centroid of the base triangle is at 1/3 of the height from the front edge.
      // The 3rd vertex is at 2/3 of the triangle height behind the front edge midpoint.
      const triHeight = (baseEdge * Math.sqrt(3)) / 2;
      // The base triangle has 2 front verts at z=0 (aligned with metatron) and 1 rear at z=-depth.
      // To show full 3D from the side, we mirror the rear vertex to +Z as well,
      // creating a double-tetrahedron (2 tets sharing the same apex and front edge).
      const maxZ = f * s * 0.88;
      const depthZ = Math.min((2 / 3) * triHeight, maxZ);

      const upMidX = (outerNodes3D[4][0] + outerNodes3D[2][0]) / 2;
      const upMidY = (outerNodes3D[4][1] + outerNodes3D[2][1]) / 2;
      const dnMidX = (outerNodes3D[5][0] + outerNodes3D[1][0]) / 2;
      const dnMidY = (outerNodes3D[5][1] + outerNodes3D[1][1]) / 2;

      // Green (upward/male) — back half + front half
      const upVertsBack = [
        new T.Vector3(outerNodes3D[0][0], outerNodes3D[0][1], 0), // apex
        new T.Vector3(outerNodes3D[4][0], outerNodes3D[4][1], 0), // base: bot-left
        new T.Vector3(outerNodes3D[2][0], outerNodes3D[2][1], 0), // base: bot-right
        new T.Vector3(upMidX, upMidY, -depthZ), // base: rear (behind)
      ];
      const upVertsFront = [
        new T.Vector3(outerNodes3D[0][0], outerNodes3D[0][1], 0), // apex (shared)
        new T.Vector3(outerNodes3D[4][0], outerNodes3D[4][1], 0), // base: bot-left (shared)
        new T.Vector3(outerNodes3D[2][0], outerNodes3D[2][1], 0), // base: bot-right (shared)
        new T.Vector3(upMidX, upMidY, depthZ), // base: front (mirrored)
      ];

      // Orange (downward/female) — back half + front half
      const dnVertsBack = [
        new T.Vector3(outerNodes3D[3][0], outerNodes3D[3][1], 0), // apex
        new T.Vector3(outerNodes3D[5][0], outerNodes3D[5][1], 0), // base: top-left
        new T.Vector3(outerNodes3D[1][0], outerNodes3D[1][1], 0), // base: top-right
        new T.Vector3(dnMidX, dnMidY, -depthZ), // base: rear (behind)
      ];
      const dnVertsFront = [
        new T.Vector3(outerNodes3D[3][0], outerNodes3D[3][1], 0), // apex (shared)
        new T.Vector3(outerNodes3D[5][0], outerNodes3D[5][1], 0), // base: top-left (shared)
        new T.Vector3(outerNodes3D[1][0], outerNodes3D[1][1], 0), // base: top-right (shared)
        new T.Vector3(dnMidX, dnMidY, depthZ), // base: front (mirrored)
      ];

      // Build wireframe tetrahedron with golden-tinted glowing glass faces
      const buildTet = (verts, isMale) => {
        const group = new T.Group();

        // Golden sunray tint colors
        const goldFace = 0xf1eaff; // warm golden white for glass faces
        const goldEmissive = 0xb59cff; // soft amber glow from within
        const goldEdge = 0xe6dcff; // subtle golden edge color
        const goldInner = 0xdcd0ff; // inner glow layer

        // Glowing glass faces — multiple layers for depth and luminosity
        const faceGeo = new T.BufferGeometry();
        const pos = new Float32Array(verts.length * 3);
        verts.forEach((v, i) => {
          pos[i * 3] = v.x;
          pos[i * 3 + 1] = v.y;
          pos[i * 3 + 2] = v.z;
        });
        faceGeo.setAttribute("position", new T.BufferAttribute(pos, 3));
        faceGeo.setIndex([0, 1, 2, 0, 2, 3, 0, 3, 1, 1, 3, 2]);
        faceGeo.computeVertexNormals();

        // Base glass layer — translucent golden with sunray warmth
        group.add(
          new T.Mesh(
            faceGeo.clone(),
            new T.MeshPhongMaterial({
              color: goldFace,
              emissive: goldEmissive,
              emissiveIntensity: 0.18,
              transparent: true,
              opacity: isMale ? 0.14 : 0.11,
              side: T.DoubleSide,
              depthWrite: false,
              shininess: 200,
              specular: 0xf0e8ff,
            })
          )
        );

        // Inner glow layer — warm golden lit-from-within
        const glowGeo = faceGeo.clone();
        group.add(
          new T.Mesh(
            glowGeo,
            new T.MeshBasicMaterial({
              color: goldInner,
              transparent: true,
              opacity: isMale ? 0.06 : 0.04,
              side: T.DoubleSide,
              depthWrite: false,
            })
          )
        );

        // Golden-tinted white edges
        const edges = [
          [0, 1],
          [0, 2],
          [0, 3],
          [1, 2],
          [2, 3],
          [3, 1],
        ];
        for (const [a, b] of edges) {
          const geo = new T.BufferGeometry().setFromPoints([
            verts[a],
            verts[b],
          ]);
          group.add(
            new T.Line(
              geo,
              new T.LineBasicMaterial({
                color: goldEdge,
                transparent: true,
                opacity: isMale ? 1.0 : 0.88,
              })
            )
          );
        }

        return group;
      };

      // Build both halves for each tetrahedron
      const upGroup = new T.Group();
      const upBack = buildTet(upVertsBack, true);
      const upFront = buildTet(upVertsFront, true);
      upGroup.add(upBack);
      upGroup.add(upFront);

      const downGroup = new T.Group();
      const dnBack = buildTet(dnVertsBack, false);
      const dnFront = buildTet(dnVertsFront, false);
      downGroup.add(dnBack);
      downGroup.add(dnFront);

      // (Vertex dots are rendered in the SVG metatron layer so they pulse with it)

      // ── Central radiant orb — soft diffuse glow, NOT opaque ──
      // Soft core — low opacity, diffuse rather than solid
      const orbCore = new T.Mesh(
        new T.SphereGeometry(0.18, 24, 24),
        new T.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.25,
        })
      );
      scene.add(orbCore);
      // Mid glow layer — gentle spread
      const orbMid = new T.Mesh(
        new T.SphereGeometry(0.7, 24, 24),
        new T.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.06,
        })
      );
      scene.add(orbMid);
      // Outer radial spread — covers most of the geometry
      const orbOuter = new T.Mesh(
        new T.SphereGeometry(1.8, 32, 32),
        new T.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.03,
        })
      );
      scene.add(orbOuter);

      // Master group — only contains the spinning tetrahedrons
      const master = new T.Group();
      master.add(upGroup);
      master.add(downGroup);
      master.rotation.x = 0;
      master.rotation.y = 0;
      scene.add(master);

      // ── Drag interaction — controls spin speed, not rotation axis ──
      let drag = false,
        lastX = 0;
      const getX = (e) => (e.touches ? e.touches[0].clientX : e.clientX);
      const onDown = (e) => {
        drag = true;
        lastX = getX(e);
      };
      const onUp = () => {
        drag = false;
      };
      const onMove = (e) => {
        if (!drag) return;
        if (e.cancelable) e.preventDefault(); // prevent page scroll while dragging
        const cx = getX(e);
        const delta = (cx - lastX) * 0.3;
        const newSpeed = Math.max(
          0,
          Math.min(100, infinitySpinRef.current + delta)
        );
        infinitySpinRef.current = newSpeed;
        lastX = cx;
      };
      canvas.addEventListener("mousedown", onDown);
      canvas.addEventListener("touchstart", onDown, { passive: false });
      window.addEventListener("mouseup", onUp);
      window.addEventListener("touchend", onUp);
      window.addEventListener("mousemove", onMove);
      window.addEventListener("touchmove", onMove, { passive: false });

      let animId;
      const animate = () => {
        animId = requestAnimationFrame(animate);
        // Exponential speed curve: gentle at low values, extreme at max (infinity effect)
        // At 0: stopped. At 50: moderate. At 100: blazing fast.
        const rawPct = infinitySpinRef.current / 100;
        const spd = rawPct * rawPct * rawPct * 0.35; // cubic curve: 0→0, 0.5→0.044, 1.0→0.35 rad/frame
        // Male (upward/green) = counterclockwise
        upGroup.rotation.y -= spd;
        // Female (downward/orange) = clockwise — same speed opposite direction
        downGroup.rotation.y += spd;
        // Pulsating orb — gentle diffuse breathing, restrained at max
        const t = Date.now() * 0.001;
        const pulse = 1 + Math.sin(t * 1.5) * 0.12;
        orbCore.scale.setScalar(pulse);
        orbCore.material.opacity = 0.12 + Math.sin(t * 1.5) * 0.04;
        orbMid.scale.setScalar(1 + Math.sin(t * 1.2) * 0.18);
        orbMid.material.opacity = 0.025 + Math.sin(t * 1.2) * 0.012;
        orbOuter.scale.setScalar(1 + Math.sin(t * 0.8) * 0.2);
        orbOuter.material.opacity = 0.008 + Math.sin(t * 0.8) * 0.005;
        renderer.render(scene, camera);
      };
      animate();

      const onResize = () => {
        const nw = canvas.offsetWidth || 300;
        const nh = canvas.offsetHeight || 300;
        renderer.setSize(nw, nh);
      };
      window.addEventListener("resize", onResize);

      return () => {
        cancelAnimationFrame(animId);
        canvas.removeEventListener("mousedown", onDown);
        canvas.removeEventListener("touchstart", onDown);
        window.removeEventListener("mouseup", onUp);
        window.removeEventListener("touchend", onUp);
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("touchmove", onMove);
        window.removeEventListener("resize", onResize);
        // Dispose all geometries and materials to prevent memory leaks
        scene.traverse((obj) => {
          if (obj.geometry) obj.geometry.dispose();
          if (obj.material) {
            if (Array.isArray(obj.material))
              obj.material.forEach((m) => m.dispose());
            else obj.material.dispose();
          }
        });
        renderer.dispose();
      };
    };
    if (window.THREE) {
      cleanup = initScene() || (() => {});
    } else {
      const s = document.createElement("script");
      s.src =
        "https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js";
      s.onload = () => {
        cleanup = initScene() || (() => {});
      };
      document.head.appendChild(s);
    }
    return () => {
      cleanup();
    };
  }, [activeTab]);

  // ── localStorage rehydration ─────────────────────────────────────────────
  useEffect(() => {
    const apiKeyData = localStorage.getItem("coingecko-api-key");
    const cryptoData = localStorage.getItem("crypto-holdings");
    const fiatData = localStorage.getItem("fiat-holdings");
    const historyData = localStorage.getItem("portfolio-history");
    const quoteData = localStorage.getItem("inspiring-quote");
    const coverData = localStorage.getItem("cover-image");

    if (apiKeyData) {
      setApiKey(apiKeyData);
      setShowApiInput(false);
    }
    if (cryptoData) setCryptoHoldings(JSON.parse(cryptoData));
    if (fiatData) {
      const parsed = JSON.parse(fiatData);
      if (parsed.cashReserve !== undefined && parsed.cash === undefined) {
        parsed.cash = parsed.cashReserve;
        delete parsed.cashReserve;
      }
      const complete = {
        cash: parsed.cash || 0,
        primeXBT: parsed.primeXBT || 0,
        posbSpending: parsed.posbSpending || 0,
        ocbc: parsed.ocbc || 0,
      };
      localStorage.setItem("fiat-holdings", JSON.stringify(complete));
      setFiatHoldings(complete);
    }
    if (historyData) {
      const parsed = JSON.parse(historyData);
      setHistory(
        parsed.map((e) =>
          e.cashReserve !== undefined && e.cash === undefined
            ? { ...e, cash: e.cashReserve }
            : e
        )
      );
    }
    setInspiringQuote(quoteData || "Every moment is a fresh beginning.");
    if (coverData) setCoverImage(coverData);
    const createQuoteData = localStorage.getItem("create-quote");
    if (createQuoteData) setCreateQuote(createQuoteData);
    const streamsData = localStorage.getItem("revenue-streams");
    if (streamsData) setRevenueStreams(JSON.parse(streamsData));
    const iq = localStorage.getItem("infinity-quote");
    if (iq) setInfinityQuote(iq);
    const actsData = localStorage.getItem("acts-of-love");
    if (actsData) setActsOfLove(JSON.parse(actsData));
    const hq = localStorage.getItem("heart-quote");
    if (hq) setHeartQuote(hq);
  }, []);

  // ── API key save ─────────────────────────────────────────────────────────
  const saveApiKey = (key) => {
    localStorage.setItem("coingecko-api-key", key);
    setApiKey(key);
    setShowApiInput(false);
    fetchPrices(key);
  };

  const saveQuote = () => {
    localStorage.setItem("inspiring-quote", inspiringQuote);
    setIsEditingQuote(false);
  };

  const handleCoverImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const b64 = reader.result;
      setCoverImage(b64);
      localStorage.setItem("cover-image", b64);
      setIsEditingCover(false);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };
  const removeCoverImage = () => {
    setCoverImage("");
    localStorage.removeItem("cover-image");
    setIsEditingCover(false);
  };

  // ── Price fetch ──────────────────────────────────────────────────────────
  const fetchPrices = async (key = apiKey) => {
    if (!key) {
      setError("Please enter your CoinGecko API key");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const url = `https://api.coingecko.com/api/v3/simple/price?ids=usd-coin,ethereum,solana,binancecoin&vs_currencies=sgd&precision=full&x_cg_demo_api_key=${key}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("API error: " + response.status);
      const data = await response.json();
      const newPrices = {
        USDC: data["usd-coin"]?.sgd || 0,
        ETH: data.ethereum?.sgd || 0,
        SOL: data.solana?.sgd || 0,
        BNB: data.binancecoin?.sgd || 0,
      };
      if (newPrices.ETH === 0) throw new Error("No price data returned");
      setPrices(newPrices);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (apiKey) {
      fetchPrices();
      const iv = setInterval(() => fetchPrices(), 120000);
      return () => clearInterval(iv);
    }
  }, [apiKey]);

  // ── Computed totals ──────────────────────────────────────────────────────
  const cryptoTotal =
    (parseFloat(cryptoHoldings.USDC) || 0) * prices.USDC +
    (parseFloat(cryptoHoldings.ETH) || 0) * prices.ETH +
    (parseFloat(cryptoHoldings.SOL) || 0) * prices.SOL +
    (parseFloat(cryptoHoldings.BNB) || 0) * prices.BNB +
    (parseFloat(cryptoHoldings.SGD) || 0);
  const fiatTotal = Object.values(fiatHoldings).reduce(
    (s, v) => s + (parseFloat(v) || 0),
    0
  );
  const totalPortfolio = cryptoTotal + fiatTotal;
  const multiplier = parseFloat(manifestationMultiplier) || 1;
  const displayTotal = totalPortfolio * multiplier;
  const displayCrypto = cryptoTotal * multiplier;
  const displayFiat = fiatTotal * multiplier;
  const formatDisplayNumber = (n) =>
    n.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // ── Holdings logic ───────────────────────────────────────────────────────
  const saveHoldings = (newCrypto, newFiat) => {
    const ts = new Date().toISOString();
    const pc = {
      USDC: parseFloat(newCrypto.USDC) || 0,
      SGD: parseFloat(newCrypto.SGD) || 0,
      ETH: parseFloat(newCrypto.ETH) || 0,
      SOL: parseFloat(newCrypto.SOL) || 0,
      BNB: parseFloat(newCrypto.BNB) || 0,
    };
    const pf = {
      cash: parseFloat(newFiat.cash) || 0,
      primeXBT: parseFloat(newFiat.primeXBT) || 0,
      posbSpending: parseFloat(newFiat.posbSpending) || 0,
      ocbc: parseFloat(newFiat.ocbc) || 0,
    };
    const oc = JSON.parse(localStorage.getItem("crypto-holdings") || "{}");
    const oldFiat = JSON.parse(localStorage.getItem("fiat-holdings") || "{}");
    localStorage.setItem("crypto-holdings", JSON.stringify(pc));
    localStorage.setItem("fiat-holdings", JSON.stringify(pf));
    const cv =
      pc.USDC * prices.USDC +
      pc.ETH * prices.ETH +
      pc.SOL * prices.SOL +
      pc.BNB * prices.BNB +
      pc.SGD;
    const fv = Object.values(pf).reduce((s, v) => s + v, 0);
    // Track each asset individually — store token amounts (not SGD), so price fluctuations don't create false entries
    const usdcChanged = (parseFloat(oc.USDC) || 0) !== pc.USDC;
    const sgdChanged = (parseFloat(oc.SGD) || 0) !== pc.SGD;
    const ethChanged = (parseFloat(oc.ETH) || 0) !== pc.ETH;
    const solChanged = (parseFloat(oc.SOL) || 0) !== pc.SOL;
    const bnbChanged = (parseFloat(oc.BNB) || 0) !== pc.BNB;
    const cashChanged = (parseFloat(oldFiat.cash) || 0) !== pf.cash;
    const primeChanged = (parseFloat(oldFiat.primeXBT) || 0) !== pf.primeXBT;
    const posbChanged =
      (parseFloat(oldFiat.posbSpending) || 0) !== pf.posbSpending;
    const ocbcChanged = (parseFloat(oldFiat.ocbc) || 0) !== pf.ocbc;
    const anyChanged =
      usdcChanged ||
      sgdChanged ||
      ethChanged ||
      solChanged ||
      bnbChanged ||
      cashChanged ||
      primeChanged ||
      posbChanged ||
      ocbcChanged;
    if (anyChanged) {
      const entry = {
        date: ts,
        total: cv + fv,
        fiat: fv,
        // Crypto: store token amounts per asset (null = unchanged)
        usdc: usdcChanged ? pc.USDC : null,
        sgdCoin: sgdChanged ? pc.SGD : null,
        eth: ethChanged ? pc.ETH : null,
        sol: solChanged ? pc.SOL : null,
        bnb: bnbChanged ? pc.BNB : null,
        // Fiat: store SGD amounts (null = unchanged) — same keys as old format
        cash: cashChanged ? pf.cash : null,
        primeXBT: primeChanged ? pf.primeXBT : null,
        posb: posbChanged ? pf.posbSpending : null,
        ocbc: ocbcChanged ? pf.ocbc : null,
        // Aggregated crypto SGD for the line chart
        crypto:
          usdcChanged || sgdChanged || ethChanged || solChanged || bnbChanged
            ? cv
            : null,
      };
      // Use functional updater to always read the latest history state — prevents stale closure overwriting existing entries
      setHistory((prev) => {
        const nh = [...prev, entry];
        localStorage.setItem("portfolio-history", JSON.stringify(nh));
        return nh;
      });
    }
    setPendingCryptoChanges(false);
    setPendingFiatChanges(false);
  };
  const handleCryptoChange = (asset, value) => {
    setCryptoHoldings((h) => ({ ...h, [asset]: value === "" ? 0 : value }));
    setPendingCryptoChanges(true);
  };
  const handleFiatChange = (acct, value) => {
    setFiatHoldings((h) => ({ ...h, [acct]: value === "" ? 0 : value }));
    setPendingFiatChanges(true);
  };
  const saveCryptoHoldings = () => saveHoldings(cryptoHoldings, fiatHoldings);
  const saveFiatHoldings = () => saveHoldings(cryptoHoldings, fiatHoldings);

  // ── Delete entry ─────────────────────────────────────────────────────────
  const deleteEntry = (entryDate) => {
    const nh = history.filter((e) => e.date !== entryDate);
    const del = history.find((e) => e.date === entryDate);
    if (del) {
      // Helper: find the most recent value for a given key in the remaining history
      const last = (key) => {
        const found = nh
          .filter((e) => e[key] !== null && e[key] !== undefined)
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
        return found ? found[key] : null;
      };

      let lc = { ...cryptoHoldings };
      let lf = { ...fiatHoldings };

      // Crypto: revert each individual asset if it was changed in the deleted entry.
      // If no prior history entry has the individual key (old format), keep current value.
      if (del.usdc !== null && del.usdc !== undefined) {
        const v = last("usdc");
        lc.USDC = v !== null ? v : lc.USDC;
      }
      if (del.sgdCoin !== null && del.sgdCoin !== undefined) {
        const v = last("sgdCoin");
        lc.SGD = v !== null ? v : lc.SGD;
      }
      if (del.eth !== null && del.eth !== undefined) {
        const v = last("eth");
        lc.ETH = v !== null ? v : lc.ETH;
      }
      if (del.sol !== null && del.sol !== undefined) {
        const v = last("sol");
        lc.SOL = v !== null ? v : lc.SOL;
      }
      if (del.bnb !== null && del.bnb !== undefined) {
        const v = last("bnb");
        lc.BNB = v !== null ? v : lc.BNB;
      }

      // Fiat: revert to previous value; fall back to 0 if genuinely no prior entry
      if (del.cash !== null && del.cash !== undefined) {
        const v = last("cash");
        lf.cash = v !== null ? v : 0;
      }
      if (del.primeXBT !== null && del.primeXBT !== undefined) {
        const v = last("primeXBT");
        lf.primeXBT = v !== null ? v : 0;
      }
      if (del.posb !== null && del.posb !== undefined) {
        const v = last("posb");
        lf.posbSpending = v !== null ? v : 0;
      }
      if (del.ocbc !== null && del.ocbc !== undefined) {
        const v = last("ocbc");
        lf.ocbc = v !== null ? v : 0;
      }

      setCryptoHoldings(lc);
      setFiatHoldings(lf);
      localStorage.setItem("crypto-holdings", JSON.stringify(lc));
      localStorage.setItem("fiat-holdings", JSON.stringify(lf));
    }
    setHistory(nh);
    localStorage.setItem("portfolio-history", JSON.stringify(nh));
  };

  // ── Export / Import ──────────────────────────────────────────────────────
  const exportData = () => {
    const blob = new Blob(
      [
        JSON.stringify(
          {
            version: "1.0",
            exportDate: new Date().toISOString(),
            cryptoHoldings,
            fiatHoldings,
            history,
            inspiringQuote,
            createQuote,
            heartQuote,
            infinityQuote,
            revenueStreams,
            actsOfLove,
            coverImage,
            apiKey,
          },
          null,
          2
        ),
      ],
      { type: "application/json" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `creation-backup-${
      new Date().toISOString().split("T")[0]
    }.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const importData = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const d = JSON.parse(ev.target.result);
        if (d.cryptoHoldings) {
          setCryptoHoldings(d.cryptoHoldings);
          localStorage.setItem(
            "crypto-holdings",
            JSON.stringify(d.cryptoHoldings)
          );
        }
        if (d.fiatHoldings) {
          setFiatHoldings(d.fiatHoldings);
          localStorage.setItem("fiat-holdings", JSON.stringify(d.fiatHoldings));
        }
        if (d.history) {
          setHistory(d.history);
          localStorage.setItem("portfolio-history", JSON.stringify(d.history));
        }
        if (d.inspiringQuote) {
          setInspiringQuote(d.inspiringQuote);
          localStorage.setItem("inspiring-quote", d.inspiringQuote);
        }
        if (d.createQuote) {
          setCreateQuote(d.createQuote);
          localStorage.setItem("create-quote", d.createQuote);
        }
        if (d.heartQuote) {
          setHeartQuote(d.heartQuote);
          localStorage.setItem("heart-quote", d.heartQuote);
        }
        if (d.infinityQuote) {
          setInfinityQuote(d.infinityQuote);
          localStorage.setItem("infinity-quote", d.infinityQuote);
        }
        if (d.revenueStreams) {
          setRevenueStreams(d.revenueStreams);
          localStorage.setItem(
            "revenue-streams",
            JSON.stringify(d.revenueStreams)
          );
        }
        if (d.actsOfLove) {
          setActsOfLove(d.actsOfLove);
          localStorage.setItem("acts-of-love", JSON.stringify(d.actsOfLove));
        }
        if (d.coverImage) {
          setCoverImage(d.coverImage);
          localStorage.setItem("cover-image", d.coverImage);
        }
        if (d.apiKey) {
          setApiKey(d.apiKey);
          localStorage.setItem("coingecko-api-key", d.apiKey);
          setShowApiInput(false);
        }
        alert("✅ Data imported successfully!");
      } catch {
        alert("❌ Error importing data.");
      }
    };
    reader.readAsText(file);
  };

  // ── Pie data ─────────────────────────────────────────────────────────────
  const cryptoPieData = [
    {
      name: "USDC",
      value: (parseFloat(cryptoHoldings.USDC) || 0) * prices.USDC,
      color: "#86f7dc",
    },
    {
      name: "SGD",
      value: parseFloat(cryptoHoldings.SGD) || 0,
      color: "#c4b0ff",
    },
    {
      name: "ETH",
      value: (parseFloat(cryptoHoldings.ETH) || 0) * prices.ETH,
      color: "#8fb4ff",
    },
    {
      name: "SOL",
      value: (parseFloat(cryptoHoldings.SOL) || 0) * prices.SOL,
      color: "#ff9fd0",
    },
    {
      name: "BNB",
      value: (parseFloat(cryptoHoldings.BNB) || 0) * prices.BNB,
      color: "#f1e6ff",
    },
  ].filter((x) => x.value > 0);

  const fiatPieData = [
    {
      name: "Cash",
      value: parseFloat(fiatHoldings.cash) || 0,
      color: "#c4b0ff",
    },
    {
      name: "PrimeXBT",
      value: parseFloat(fiatHoldings.primeXBT) || 0,
      color: "#8fb4ff",
    },
    {
      name: "POSB",
      value: parseFloat(fiatHoldings.posbSpending) || 0,
      color: "#ff9fd0",
    },
    {
      name: "OCBC",
      value: parseFloat(fiatHoldings.ocbc) || 0,
      color: "#f1e6ff",
    },
  ].filter((x) => x.value > 0);

  // ── History filters ──────────────────────────────────────────────────────
  const filterHistory = (tf) => {
    if (tf === "all") return [...history];
    const now = new Date(),
      cut = new Date();
    if (tf === "day") cut.setDate(now.getDate() - 1);
    if (tf === "month") cut.setMonth(now.getMonth() - 1);
    if (tf === "year") cut.setFullYear(now.getFullYear() - 1);
    return history.filter((e) => new Date(e.date) >= cut);
  };
  const filteredHistory = filterHistory(timeframe).sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );
  const filteredHistoryTable = (() => {
    if (selectedCalendarDate)
      return history.filter((e) => {
        const d = new Date(e.date);
        return (
          d.getDate() === selectedCalendarDate.getDate() &&
          d.getMonth() === selectedCalendarDate.getMonth() &&
          d.getFullYear() === selectedCalendarDate.getFullYear()
        );
      });
    return filterHistory(historyView);
  })().sort((a, b) => new Date(b.date) - new Date(a.date));

  // ── Formatters ────────────────────────────────────────────────────────────
  const formatXAxisLabel = (date, index) => {
    const d = new Date(date),
      dd = d.getDate().toString().padStart(2, "0"),
      mm = (d.getMonth() + 1).toString().padStart(2, "0");
    if (index === 0) return `${dd}/${mm}`;
    const prev = new Date(filteredHistory[index - 1]?.date);
    return prev.getDate() !== d.getDate() ||
      prev.getMonth() !== d.getMonth() ||
      prev.getFullYear() !== d.getFullYear()
      ? `${dd}/${mm}`
      : "";
  };
  const formatTooltipLabel = (date) => {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1)
      .toString()
      .padStart(2, "0")} ${d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  };
  const formatValue = (v) =>
    v === null || v === undefined || isNaN(v) ? "$0.000" : "$" + v.toFixed(3);
  const formatPercentLabel = (name, pct) =>
    pct === null || pct === undefined || isNaN(pct)
      ? name + " 0.0%"
      : name + " " + (pct * 100).toFixed(1) + "%";

  // ── Calendar helpers ──────────────────────────────────────────────────────
  const getDaysInMonth = (date) => {
    const y = date.getFullYear(),
      m = date.getMonth();
    return {
      daysInMonth: new Date(y, m + 1, 0).getDate(),
      startingDayOfWeek: new Date(y, m, 1).getDay(),
      year: y,
      month: m,
    };
  };
  const hasEntryOnDate = (date) =>
    history.some((e) => {
      const d = new Date(e.date);
      return (
        d.getDate() === date.getDate() &&
        d.getMonth() === date.getMonth() &&
        d.getFullYear() === date.getFullYear()
      );
    });
  const changeCalendarMonth = (offset) => {
    const d = new Date(calendarView);
    d.setMonth(d.getMonth() + offset);
    setCalendarView(d);
  };

  // ── Shared button style ───────────────────────────────────────────────────
  const timeBtn = (active) => ({
    padding: "6px 14px",
    fontSize: "0.8rem",
    border: "1px solid rgba(255, 255, 255, 0.16)",
    borderRadius: "8px",
    background: active
      ? "linear-gradient(135deg,#86f7dc 0%,#a9fbe9 100%)"
      : "rgba(255, 255, 255, 0.15)",
    color: active ? "#06301f" : "rgba(236,228,255,0.82)",
    cursor: "pointer",
    letterSpacing: "0.3px",
    textTransform: "capitalize",
  });

  // ── Create tab computed values ────────────────────────────────────────────
  const ctMaxMonths = projectionYears * 12;
  const ctNow = new Date();
  const ctGetStartMonth = (stream) => {
    if (!stream.startDate) return 0;
    const d = new Date(stream.startDate);
    return Math.max(0, Math.round((d - ctNow) / (1000 * 60 * 60 * 24 * 30.44)));
  };
  const ctGetDurMonths = (stream) => {
    if (!stream.duration) return null;
    const n = parseFloat(stream.duration) || 0;
    return stream.durationUnit === "yr" ? Math.round(n * 12) : Math.round(n);
  };
  const ctTraj = (() => {
    const pts = [];
    for (let t = 0; t <= ctMaxMonths; t++) {
      if (t === 0) {
        pts.push(totalPortfolio);
        continue;
      }
      let val = totalPortfolio;
      for (const s of revenueStreams) {
        const sm = ctGetStartMonth(s);
        const dur = ctGetDurMonths(s);
        const rawAmt = parseFloat(s.amount) || 0;
        const amt = s.amountUnit === "yr" ? rawAmt / 12 : rawAmt;
        const cmp = (parseFloat(s.compound) || 0) / 100 / 12;
        if (s.type === "one-time") {
          if (t > sm) val += amt;
        } else {
          const endM = dur !== null ? sm + dur : Infinity;
          if (t > sm) {
            const n = Math.min(t - 1, endM - 1) - sm + 1;
            if (n > 0) {
              if (cmp === 0) {
                val += amt * n;
              } else {
                val += (amt * (Math.pow(1 + cmp, n) - 1)) / cmp;
              }
            }
          }
        }
      }
      pts.push(val);
    }
    return pts;
  })();
  const ctMinVal = Math.min(...ctTraj);
  const ctMaxVal = Math.max(...ctTraj);
  const ctRange = ctMaxVal - ctMinVal || 1;
  const CW = 520,
    CH = isMobile ? 220 : 145,
    PAD_L = 8,
    PAD_R = 8,
    PAD_T = 14,
    PAD_B = 12;
  const ctXScale = (m) => PAD_L + (m / ctMaxMonths) * (CW - PAD_L - PAD_R);
  const ctYScale = (v) =>
    PAD_T + (1 - (v - ctMinVal) / ctRange) * (CH - PAD_T - PAD_B);
  const ctPathD = ctTraj
    .map(
      (v, i) =>
        `${i === 0 ? "M" : "L"} ${ctXScale(i).toFixed(1)} ${ctYScale(v).toFixed(
          1
        )}`
    )
    .join(" ");
  const ctAreaD =
    ctPathD +
    ` L ${ctXScale(ctMaxMonths).toFixed(1)} ${CH} L ${ctXScale(0).toFixed(
      1
    )} ${CH} Z`;
  const ctClampedOrb = Math.max(0, Math.min(orbMonth, ctMaxMonths));
  const ctOrbX = ctXScale(ctClampedOrb);
  const ctOrbY = ctYScale(ctTraj[ctClampedOrb] || ctTraj[0]);
  const ctOrbVal = ctTraj[ctClampedOrb] || ctTraj[0];
  const ctHandleChartMouse = (e) => {
    if (!isDragging) return;
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const svgW = rect.width;
    const relX = (clientX - rect.left) * (CW / svgW);
    const ratio = (relX - PAD_L) / (CW - PAD_L - PAD_R);
    const m = Math.round(Math.max(0, Math.min(1, ratio)) * ctMaxMonths);
    setOrbMonth(m);
  };
  const ctMonthLabel = (m) => {
    const d = new Date(ctNow);
    d.setMonth(d.getMonth() + m);
    return d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
  };
  const ctAxisLabels = [
    0,
    Math.round(ctMaxMonths * 0.25),
    Math.round(ctMaxMonths * 0.5),
    Math.round(ctMaxMonths * 0.75),
    ctMaxMonths,
  ];
  const ctTotalMonthlyRecurring = revenueStreams
    .filter((s) => s.type === "recurring")
    .reduce((sum, s) => {
      const raw = parseFloat(s.amount) || 0;
      return sum + (s.amountUnit === "yr" ? raw / 12 : raw);
    }, 0);
  const ctTotalInflow = ctTraj[ctMaxMonths] - totalPortfolio;
  const ctSaveStream = () => {
    const id = editingStreamId || "s" + Date.now();
    const updated = editingStreamId
      ? revenueStreams.map((s) =>
          s.id === editingStreamId ? { ...streamForm, id } : s
        )
      : [...revenueStreams, { ...streamForm, id }];
    setRevenueStreams(updated);
    localStorage.setItem("revenue-streams", JSON.stringify(updated));
    setStreamForm(blankStream);
    setShowAddStream(false);
    setEditingStreamId(null);
  };
  const ctDeleteStream = (id) => {
    const updated = revenueStreams.filter((s) => s.id !== id);
    setRevenueStreams(updated);
    localStorage.setItem("revenue-streams", JSON.stringify(updated));
  };
  const ctStartEdit = (s) => {
    setStreamForm({ ...s });
    setEditingStreamId(s.id);
    setShowAddStream(true);
  };
  const ctFmtSGD = (v) =>
    "SGD $" +
    v.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  const ctSgdGlowStyle = isDragging
    ? "0 0 8px rgba(255, 255, 255, 1), 0 0 22px rgba(255, 255, 255, 1), 0 0 55px rgba(255, 255, 255, 0.75), 0 0 100px rgba(255, 255, 255, 0.55)"
    : "0 0 6px rgba(255, 255, 255, 0.7), 0 0 18px rgba(255, 255, 255, 0.35), 0 0 40px rgba(255, 255, 255, 0.18)";
  const ctOrbDistLabel =
    ctClampedOrb === 0
      ? "today"
      : ctClampedOrb < 12
      ? ctClampedOrb + " mo"
      : (ctClampedOrb / 12).toFixed(1).replace(".0", "") + " yr";
  const ctOrbFilter = isDragging
    ? "drop-shadow(0 0 10px rgba(255, 255, 255, 0.95)) drop-shadow(0 0 24px rgba(255, 255, 255, 0.7)) drop-shadow(0 0 48px rgba(196, 176, 255, 0.45))"
    : "drop-shadow(0 0 6px rgba(255, 255, 255, 0.85)) drop-shadow(0 0 16px rgba(255, 255, 255, 0.5)) drop-shadow(0 0 32px rgba(196, 176, 255, 0.28))";
  const ctViewBox = "0 0 " + CW + " " + CH;
  const ctChartCursor = isDragging ? "grabbing" : "crosshair";

  // ── API key setup screen ──────────────────────────────────────────────────
  if (showApiInput) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "transparent",
          padding: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Cosmos />
        <div
          style={{
            ...pnl,
            padding: "40px",
            maxWidth: "28rem",
            width: "100%",
            position: "relative",
            zIndex: 2,
          }}
        >
          <h1
            className="creation-title"
            style={{
              fontSize: "2rem",
              fontWeight: "200",
              color: "#ffffff",
              marginBottom: "32px",
              letterSpacing: "0.22em",
              textTransform: "uppercase",
            }}
          >
            Creation
          </h1>
          <p
            style={{
              fontSize: "0.875rem",
              color: "rgba(208, 198, 244, 0.62)",
              marginBottom: "20px",
              lineHeight: "1.6",
            }}
          >
            Enter your CoinGecko API key to begin:
          </p>
          <ol
            style={{
              fontSize: "0.875rem",
              color: "rgba(236, 228, 255, 0.82)",
              marginBottom: "28px",
              lineHeight: "1.8",
              paddingLeft: "20px",
            }}
          >
            <li>
              Visit{" "}
              <a
                href="https://www.coingecko.com/en/api"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#ffffff" }}
              >
                coingecko.com/en/api
              </a>
            </li>
            <li>Click "Get Your Free API Key"</li>
            <li>Sign up for Demo account</li>
            <li>Copy and paste your key below</li>
          </ol>
          <input
            id="akInput"
            type="password"
            placeholder="CG-xxxxxxxxxxxxxxxxxxxx"
            style={{
              width: "100%",
              padding: "12px 16px",
              background: "rgba(14, 8, 60, 0.34)",
              border: "1px solid rgba(255, 255, 255, 0.16)",
              borderRadius: "10px",
              marginBottom: "16px",
              fontSize: "0.875rem",
              color: "rgba(236, 228, 255, 0.82)",
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target.value)
                saveApiKey(e.target.value);
            }}
          />
          <button
            onClick={() => {
              const i = document.getElementById("akInput");
              if (i.value) saveApiKey(i.value);
            }}
            style={{
              width: "100%",
              padding: "12px 16px",
              background: "linear-gradient(135deg,#86f7dc 0%,#a9fbe9 100%)",
              color: "#06301f",
              borderRadius: "10px",
              border: "none",
              cursor: "pointer",
              fontWeight: "500",
              fontSize: "0.9rem",
            }}
          >
            Save &amp; Continue
          </button>
        </div>
      </div>
    );
  }

  // ── Tab navigation icons ──────────────────────────────────────────────────
  const ICON_INACTIVE = "rgba(208, 198, 244, 0.65)";
  const ICON_ACTIVE = "#ffffff";

  const tabs = [
    { id: "home", Icon: IconSun },
    { id: "create", Icon: IconSparkle },
    { id: "infinity", Icon: IconInfinity },
    { id: "charts", Icon: IconHeart },
    { id: "calendar", Icon: IconCalendar },
  ];

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: "calc(100dvh + env(safe-area-inset-bottom, 34px))",
        width: "100%",
        background: "transparent",
        paddingTop: coverImage
          ? "calc(env(safe-area-inset-top, 44px) + 8px)"
          : "calc(env(safe-area-inset-top, 44px) + 12px)",
        paddingBottom: "max(24px, env(safe-area-inset-bottom))",
        paddingLeft: "max(16px, env(safe-area-inset-left))",
        paddingRight: "max(16px, env(safe-area-inset-right))",
        fontFamily: "Inter, sans-serif",
        overflowX: "hidden",
        position: "relative",
      }}
    >
      {/* ── Iridescent cosmos backdrop (when no cover image is set) ── */}
      {!coverImage && <Cosmos />}

      {/* ── Background image layers ───────────────────────────── */}
      {coverImage && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: "calc(-1 * env(safe-area-inset-bottom, 34px))",
              overflow: "hidden",
              zIndex: 0,
            }}
          >
            <img
              src={coverImage}
              alt="bg"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                filter: "none",
                opacity: 0.85,
                objectFit: "cover",
              }}
            />
          </div>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: "calc(-1 * env(safe-area-inset-bottom, 34px))",
              background:
                "linear-gradient(180deg,transparent 0%,transparent 15%,rgba(35, 26, 102, 0.15) 35%,rgba(35, 26, 102, 0.45) 60%,rgba(35, 26, 102, 0.75) 80%,#2b1a74 95%)",
              zIndex: 1,
              pointerEvents: "none",
            }}
          />
        </>
      )}

      <div
        style={{
          maxWidth: "80rem",
          margin: "0 auto",
          width: "100%",
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* ── ⋯ cover image trigger — very top left ─────────────── */}
        {!isEditingCover && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsEditingCover(true);
            }}
            style={{
              position: "absolute",
              top: "-8px",
              left: "4px",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "1.1rem",
              color: "rgba(208, 198, 244, 0.8)",
              letterSpacing: "3px",
              opacity: 0.18,
              padding: "4px 6px",
              borderRadius: "6px",
              transition: "opacity 0.25s",
              zIndex: 50,
              lineHeight: 1,
            }}
            onMouseOver={(e) => (e.currentTarget.style.opacity = "0.72")}
            onMouseOut={(e) => (e.currentTarget.style.opacity = "0.18")}
          >
            ⋯
          </button>
        )}

        {/* ── Cover image modal ─────────────────────────────────── */}
        {isEditingCover && (
          <>
            <div
              onClick={() => setIsEditingCover(false)}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(14, 8, 60, 0.45)",
                backdropFilter: "blur(4px)",
                WebkitBackdropFilter: "blur(4px)",
                zIndex: 1999,
              }}
            />
            <div
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%,-50%)",
                zIndex: 2000,
                background:
                  "linear-gradient(160deg,rgba(62, 44, 150, 0.92),rgba(44, 30, 116, 0.92))",
                backdropFilter: "blur(24px) saturate(140%)",
                WebkitBackdropFilter: "blur(24px) saturate(140%)",
                borderRadius: "30px",
                padding: "32px",
                boxShadow:
                  "0 30px 60px -20px rgba(8, 4, 40, 0.8), inset 1px 1px 0 rgba(255, 255, 255, 0.4), inset 0 0 0 1px rgba(255, 255, 255, 0.08)",
                border: "none",
                maxWidth: "400px",
                width: "90%",
              }}
            >
              <h3
                style={{
                  fontSize: "1.1rem",
                  fontWeight: "500",
                  color: "#ffffff",
                  marginBottom: "20px",
                  textAlign: "center",
                }}
              >
                Cover Image
              </h3>
              <input
                type="file"
                id="coverInput"
                accept="image/*"
                onChange={handleCoverImageUpload}
                style={{ display: "none" }}
              />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <label
                  htmlFor="coverInput"
                  style={{
                    display: "block",
                    padding: "14px 24px",
                    background:
                      "linear-gradient(135deg,#86f7dc 0%,#a9fbe9 100%)",
                    color: "#06301f",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    textAlign: "center",
                    fontWeight: "500",
                  }}
                >
                  {coverImage ? "Change Image" : "Upload Image"}
                </label>
                {coverImage && (
                  <button
                    onClick={removeCoverImage}
                    style={{
                      padding: "14px 24px",
                      background: "rgba(255, 180, 200, 0.25)",
                      color: "#ffb4c8",
                      border: "1px solid rgba(255, 180, 200, 0.3)",
                      borderRadius: "12px",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                      fontWeight: "500",
                    }}
                  >
                    Remove Image
                  </button>
                )}
                <button
                  onClick={() => setIsEditingCover(false)}
                  style={{
                    padding: "14px 24px",
                    background: "rgba(134, 247, 220, 0.2)",
                    color: "#ffffff",
                    border: "1px solid rgba(134, 247, 220, 0.3)",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    fontWeight: "500",
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── CREATION title ────────────────────────────────────── */}
        <div
          style={{
            textAlign: "center",
            paddingTop: "28px",
            paddingBottom: "10px",
          }}
        >
          <h1
            className="creation-title"
            style={{
              fontSize: "2.8rem",
              fontWeight: "300",
              color: "#ffffff",
              letterSpacing: "0.32em",
              paddingLeft: "0.32em",
              textTransform: "uppercase",
              margin: 0,
              display: "inline-block",
              cursor: "default",
              userSelect: "none",
            }}
          >
            Creation
          </h1>
        </div>

        {/* ── Navigation pill ──────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "32px",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
              position: "relative",
              background:
                "linear-gradient(160deg,rgba(255, 255, 255, 0.15),rgba(255, 255, 255, 0.06) 60%,rgba(255, 255, 255, 0.1))",
              backdropFilter: "blur(24px) saturate(140%)",
              WebkitBackdropFilter: "blur(24px) saturate(140%)",
              border: "none",
              borderRadius: "60px",
              padding: "7px 12px",
              boxShadow:
                "0 26px 50px -28px rgba(8, 4, 40, 0.7), inset 1px 1px 0 rgba(255, 255, 255, 0.5), inset -1px -1px 0 rgba(255, 255, 255, 0.12), inset 0 0 0 1px rgba(255, 255, 255, 0.08)",
            }}
          >
            {/* Sliding glass indicator behind the active icon */}
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                top: "7px",
                left: "12px",
                width: "56px",
                height: "46px",
                borderRadius: "40px",
                background:
                  "linear-gradient(160deg,rgba(255, 255, 255, 0.24),rgba(255, 255, 255, 0.07))",
                boxShadow:
                  "0 0 22px -2px rgba(255, 255, 255, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.34)",
                transform: `translateX(${
                  tabs.findIndex((t) => t.id === activeTab) * 62
                }px)`,
                transition: "transform 0.55s cubic-bezier(0.3,1.35,0.45,1)",
                pointerEvents: "none",
              }}
            />
            {tabs.map(({ id, Icon }) => {
              const isActive = activeTab === id;
              return (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  style={{
                    position: "relative",
                    width: "56px",
                    height: "46px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    borderRadius: "40px",
                    padding: 0,
                    transition: "transform 0.15s ease",
                  }}
                  onMouseOver={(e) => {
                    if (!isActive)
                      e.currentTarget.style.transform = "scale(1.08)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  {/* Icon */}
                  <span
                    style={{
                      position: "relative",
                      zIndex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transform: "scale(1.18)",
                      filter: isActive
                        ? "drop-shadow(0 0 6px rgba(255, 255, 255, 0.85))"
                        : "none",
                      transition: "filter 0.35s ease",
                    }}
                  >
                    <Icon color={isActive ? ICON_ACTIVE : ICON_INACTIVE} />
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ════════════════════════════════════════════════════════
            TAB: HOME  ☀️
        ════════════════════════════════════════════════════════ */}
        {activeTab === "home" && (
          <>
            {/* Quote + totals + refresh panel */}
            <div style={{ ...pnl, marginBottom: "14px" }}>
              {/* Quote */}
              <div style={{ marginBottom: "22px" }}>
                {!isEditingQuote ? (
                  <div
                    onClick={() => setIsEditingQuote(true)}
                    style={{
                      padding: "18px 32px",
                      cursor: "pointer",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%,-50%)",
                        width: "90%",
                        height: "95%",
                        background:
                          "radial-gradient(ellipse at center,rgba(255, 255, 255, 0.09) 0%,rgba(255, 255, 255, 0.2) 40%,transparent 70%)",
                        filter: "blur(20px)",
                        zIndex: 0,
                        pointerEvents: "none",
                        opacity: 0.25,
                      }}
                    />
                    <p
                      style={{
                        fontSize: "1.02rem",
                        color: "#ffffff",
                        fontStyle: "italic",
                        textAlign: "center",
                        margin: 0,
                        lineHeight: "1.7",
                        fontWeight: "400",
                        textShadow: "0 0 12px rgba(255, 255, 255, 0.3)",
                        position: "relative",
                        zIndex: 1,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {'"' + (inspiringQuote || "").trimEnd() + '"'}
                    </p>
                  </div>
                ) : (
                  <div>
                    <textarea
                      value={inspiringQuote}
                      onChange={(e) => setInspiringQuote(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "16px",
                        background: "rgba(14, 8, 60, 0.34)",
                        border: "1px solid rgba(255, 255, 255, 0.16)",
                        borderRadius: "12px",
                        fontSize: "1rem",
                        color: "rgba(236, 228, 255, 0.82)",
                        fontStyle: "italic",
                        resize: "vertical",
                        minHeight: "80px",
                        fontFamily: "inherit",
                        whiteSpace: "pre-wrap",
                      }}
                      placeholder="Enter your inspiring quote..."
                    />
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        marginTop: "10px",
                        justifyContent: "flex-end",
                      }}
                    >
                      <button
                        onClick={() => {
                          setInspiringQuote(
                            localStorage.getItem("inspiring-quote") ||
                              "Every moment is a fresh beginning."
                          );
                          setIsEditingQuote(false);
                        }}
                        style={{
                          padding: "8px 16px",
                          background: "rgba(255, 255, 255, 0.1)",
                          color: "rgba(236, 228, 255, 0.82)",
                          border: "1px solid rgba(255, 255, 255, 0.16)",
                          borderRadius: "9px",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveQuote}
                        style={{
                          padding: "8px 16px",
                          background:
                            "linear-gradient(135deg,#86f7dc 0%,#a9fbe9 100%)",
                          color: "#06301f",
                          border: "none",
                          borderRadius: "9px",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                        }}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Totals grid */}
              <div
                className="grid-3"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3,1fr)",
                  gap: "14px",
                  marginBottom: "16px",
                }}
              >
                {[
                  { label: "Total", val: displayTotal },
                  { label: "Crypto", val: displayCrypto },
                  { label: "Fiat", val: displayFiat },
                ].map(({ label, val }) => (
                  <div
                    key={label}
                    style={{
                      background: "rgba(255, 255, 255, 0.075)",
                      borderRadius: "18px",
                      padding: "20px",
                      border: "1px solid rgba(255, 255, 255, 0.12)",
                      boxShadow:
                        "0 3px 12px rgba(8, 4, 40, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.25)",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "0.68rem",
                        color: "rgba(208, 198, 244, 0.68)",
                        fontWeight: "500",
                        letterSpacing: "0.8px",
                        textTransform: "uppercase",
                        margin: "0 0 8px 0",
                      }}
                    >
                      {label}
                    </p>
                    <p
                      style={{
                        fontSize: "1.45rem",
                        fontWeight: "400",
                        color: "#ffffff",
                        margin: 0,
                        textShadow: "0 1px 6px rgba(255, 255, 255, 0.45)",
                      }}
                    >
                      SGD ${formatDisplayNumber(val)}
                    </p>
                  </div>
                ))}
              </div>

              {/* Manifestation multiplier — slider */}
              <div
                style={{
                  marginBottom: "18px",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {/* Ambient glow behind whole slider area */}
                <div
                  style={{
                    position: "absolute",
                    top: "-12px",
                    bottom: "-12px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "380px",
                    maxWidth: "92%",
                    background:
                      "radial-gradient(ellipse at center, rgba(255, 255, 255, 0.3) 0%, rgba(236, 228, 255, 0.12) 45%, transparent 70%)",
                    filter: "blur(10px)",
                    pointerEvents: "none",
                    borderRadius: "50%",
                  }}
                />

                {/* Label */}
                <label
                  style={{
                    fontSize: "0.78rem",
                    color: "rgba(255, 255, 255, 0.85)",
                    fontWeight: "300",
                    letterSpacing: "0.35px",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  Manifestation Multiplier
                </label>

                {/* Slider row: hollow sun — track — filled sun */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    width: "100%",
                    maxWidth: "360px",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  {/* Left icon: sun with hollow circle — glowing white */}
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 20 20"
                    fill="none"
                    className="sun-icon"
                    style={{ flexShrink: 0 }}
                  >
                    <circle
                      cx="10"
                      cy="10"
                      r="3.4"
                      stroke="rgba(255, 255, 255, 0.9)"
                      strokeWidth="1.3"
                    />
                    <line
                      x1="10"
                      y1="1.5"
                      x2="10"
                      y2="4.2"
                      stroke="rgba(255, 255, 255, 0.9)"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                    <line
                      x1="10"
                      y1="15.8"
                      x2="10"
                      y2="18.5"
                      stroke="rgba(255, 255, 255, 0.9)"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                    <line
                      x1="1.5"
                      y1="10"
                      x2="4.2"
                      y2="10"
                      stroke="rgba(255, 255, 255, 0.9)"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                    <line
                      x1="15.8"
                      y1="10"
                      x2="18.5"
                      y2="10"
                      stroke="rgba(255, 255, 255, 0.9)"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                    <line
                      x1="3.9"
                      y1="3.9"
                      x2="5.7"
                      y2="5.7"
                      stroke="rgba(255, 255, 255, 0.9)"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                    <line
                      x1="14.3"
                      y1="14.3"
                      x2="16.1"
                      y2="16.1"
                      stroke="rgba(255, 255, 255, 0.9)"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                    <line
                      x1="3.9"
                      y1="16.1"
                      x2="5.7"
                      y2="14.3"
                      stroke="rgba(255, 255, 255, 0.9)"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                    <line
                      x1="14.3"
                      y1="5.7"
                      x2="16.1"
                      y2="3.9"
                      stroke="rgba(255, 255, 255, 0.9)"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                  </svg>

                  {/* Bare glowing track */}
                  <input
                    type="range"
                    min="0"
                    max="20"
                    step="1"
                    value={(() => {
                      const vals = [
                        1, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65,
                        70, 75, 80, 85, 90, 95, 100,
                      ];
                      const cur = parseFloat(manifestationMultiplier) || 1;
                      const idx = vals.indexOf(cur);
                      return idx >= 0 ? idx : 0;
                    })()}
                    onChange={(e) => {
                      const vals = [
                        1, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65,
                        70, 75, 80, 85, 90, 95, 100,
                      ];
                      setManifestationMultiplier(
                        String(vals[parseInt(e.target.value)])
                      );
                    }}
                    className="manifest-slider"
                    style={{ flex: 1, display: "block" }}
                  />

                  {/* Right icon: sun with filled circle — glowing white, brighter */}
                  <svg
                    width="34"
                    height="34"
                    viewBox="0 0 20 20"
                    fill="none"
                    className="sun-icon"
                    style={{ flexShrink: 0 }}
                  >
                    <circle
                      cx="10"
                      cy="10"
                      r="3.4"
                      stroke="rgba(255, 255, 255, 0.95)"
                      strokeWidth="1.3"
                      fill="rgba(255, 255, 255, 0.95)"
                    />
                    <line
                      x1="10"
                      y1="1.5"
                      x2="10"
                      y2="4.2"
                      stroke="rgba(255, 255, 255, 0.95)"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                    <line
                      x1="10"
                      y1="15.8"
                      x2="10"
                      y2="18.5"
                      stroke="rgba(255, 255, 255, 0.95)"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                    <line
                      x1="1.5"
                      y1="10"
                      x2="4.2"
                      y2="10"
                      stroke="rgba(255, 255, 255, 0.95)"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                    <line
                      x1="15.8"
                      y1="10"
                      x2="18.5"
                      y2="10"
                      stroke="rgba(255, 255, 255, 0.95)"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                    <line
                      x1="3.9"
                      y1="3.9"
                      x2="5.7"
                      y2="5.7"
                      stroke="rgba(255, 255, 255, 0.95)"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                    <line
                      x1="14.3"
                      y1="14.3"
                      x2="16.1"
                      y2="16.1"
                      stroke="rgba(255, 255, 255, 0.95)"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                    <line
                      x1="3.9"
                      y1="16.1"
                      x2="5.7"
                      y2="14.3"
                      stroke="rgba(255, 255, 255, 0.95)"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                    <line
                      x1="14.3"
                      y1="5.7"
                      x2="16.1"
                      y2="3.9"
                      stroke="rgba(255, 255, 255, 0.95)"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>

                {/* Value label */}
                {(parseFloat(manifestationMultiplier) || 1) !== 1 && (
                  <span
                    style={{
                      fontSize: "0.72rem",
                      color: "rgba(255, 255, 255, 0.8)",
                      fontStyle: "italic",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    {`${parseFloat(manifestationMultiplier)}x growth vision`}
                  </span>
                )}
              </div>

              {/* Status + refresh */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                <div style={{ fontSize: "0.7rem", color: "rgba(208, 198, 244, 0.62)" }}>
                  {lastUpdate && (
                    <span>
                      Updated {lastUpdate.toLocaleString()} · {history.length}{" "}
                      entries
                    </span>
                  )}
                  {error && (
                    <span style={{ color: "#ffb4c8", marginLeft: "8px" }}>
                      {error}
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => {
                      fetchPrices();
                    }}
                    disabled={loading}
                    className="refresh-btn"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "4px",
                      background: "none",
                      border: "none",
                      cursor: loading ? "default" : "pointer",
                      opacity: loading ? 0.45 : 1,
                      transition: "opacity 0.2s",
                    }}
                    title="Refresh prices"
                  >
                    <svg
                      width="26"
                      height="26"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{
                        flexShrink: 0,
                        animation: loading
                          ? "refreshSpin 0.85s linear infinite"
                          : "none",
                      }}
                    >
                      <path
                        d="M4 12a8 8 0 1 1 1.5 4.8"
                        stroke="#ffffff"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        fill="none"
                      />
                      <polyline
                        points="4,7 4,12 9,12"
                        stroke="#ffffff"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Crypto + Fiat portfolio inputs */}
            <div
              className="grid-2"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2,1fr)",
                gap: "20px",
                marginBottom: "20px",
              }}
            >
              {/* Crypto */}
              <div style={pnl}>
                <h2
                  style={{
                    fontSize: "1.05rem",
                    fontWeight: "400",
                    color: "#ffffff",
                    marginBottom: "18px",
                    letterSpacing: "0.3px",
                  }}
                >
                  Crypto Portfolio
                </h2>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "14px",
                  }}
                >
                  {Object.entries(cryptoHoldings).map(([asset, amount]) => (
                    <div
                      key={asset}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <label
                          style={{
                            fontSize: "0.85rem",
                            fontWeight: "400",
                            color: "#ffffff",
                          }}
                        >
                          {asset}
                        </label>
                        <p
                          style={{
                            fontSize: "0.68rem",
                            color: "rgba(208, 198, 244, 0.62)",
                            margin: "2px 0 0 0",
                          }}
                        >
                          {asset !== "SGD" &&
                            `SGD $${prices[asset]?.toFixed(3) || "0.000"}`}
                        </p>
                      </div>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={amount === 0 || amount === "0" ? "" : amount}
                        placeholder="0"
                        onChange={(e) =>
                          handleCryptoChange(asset, e.target.value)
                        }
                        onWheel={(e) => e.target.blur()}
                        style={{
                          width: "105px",
                          padding: "9px 11px",
                          border: "1px solid rgba(134, 247, 220, 0.25)",
                          borderRadius: "10px",
                          background: "rgba(14, 8, 60, 0.34)",
                          color: "rgba(236, 228, 255, 0.82)",
                          fontSize: "0.875rem",
                          textAlign: "right",
                        }}
                      />
                      <span
                        style={{
                          fontSize: "0.82rem",
                          fontWeight: "400",
                          color:
                            (parseFloat(amount) || 0) === 0
                              ? "rgba(134, 247, 220, 0.22)"
                              : "#ffffff",
                          width: "95px",
                          textAlign: "right",
                        }}
                      >
                        $
                        {(asset === "SGD"
                          ? parseFloat(amount) || 0
                          : (parseFloat(amount) || 0) * (prices[asset] || 0)
                        ).toFixed(3)}
                      </span>
                    </div>
                  ))}
                </div>
                {pendingCryptoChanges && (
                  <button
                    onClick={saveCryptoHoldings}
                    style={{
                      width: "100%",
                      marginTop: "14px",
                      padding: "10px",
                      background:
                        "linear-gradient(135deg,#86f7dc 0%,#a9fbe9 100%)",
                      color: "#06301f",
                      borderRadius: "10px",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "0.875rem",
                      fontWeight: "500",
                    }}
                  >
                    Save Changes
                  </button>
                )}
              </div>

              {/* Fiat */}
              <div style={pnl}>
                <h2
                  style={{
                    fontSize: "1.05rem",
                    fontWeight: "400",
                    color: "#ffffff",
                    marginBottom: "18px",
                    letterSpacing: "0.3px",
                  }}
                >
                  Fiat Portfolio
                </h2>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "14px",
                  }}
                >
                  {Object.entries(fiatHoldings).map(([acct, amount]) => (
                    <div
                      key={acct}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <label
                        style={{
                          fontSize: "0.85rem",
                          fontWeight: "400",
                          color: "#ffffff",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {acct === "cash"
                          ? "Cash"
                          : acct === "primeXBT"
                          ? "PrimeXBT"
                          : acct === "posbSpending"
                          ? "POSB"
                          : "OCBC"}
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={amount === 0 || amount === "0" ? "" : amount}
                        placeholder="0"
                        onChange={(e) => handleFiatChange(acct, e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        style={{
                          width: "105px",
                          padding: "9px 11px",
                          border: "1px solid rgba(196, 176, 255, 0.25)",
                          borderRadius: "10px",
                          background: "rgba(14, 8, 60, 0.34)",
                          color: "rgba(236, 228, 255, 0.82)",
                          fontSize: "0.875rem",
                          textAlign: "right",
                        }}
                      />
                      <span
                        style={{
                          fontSize: "0.82rem",
                          fontWeight: "400",
                          color:
                            (parseFloat(amount) || 0) === 0
                              ? "rgba(196, 176, 255, 0.24)"
                              : "#ffffff",
                          width: "95px",
                          textAlign: "right",
                        }}
                      >
                        ${(parseFloat(amount) || 0).toFixed(3)}
                      </span>
                    </div>
                  ))}
                </div>
                {pendingFiatChanges && (
                  <button
                    onClick={saveFiatHoldings}
                    style={{
                      width: "100%",
                      marginTop: "14px",
                      padding: "10px",
                      background:
                        "linear-gradient(135deg,#c4b0ff 0%,#dccbff 100%)",
                      color: "#06301f",
                      borderRadius: "10px",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "0.875rem",
                      fontWeight: "500",
                    }}
                  >
                    Save Changes
                  </button>
                )}
              </div>
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════════════════
            TAB: CREATE  ✨
        ════════════════════════════════════════════════════════ */}
        {activeTab === "create" && (
          <>
            {/* ── Panel 1: Portfolio Trajectory ── */}
            <div style={{ ...pnl, marginBottom: "14px" }}>
              {/* Large glowing SGD value above chart */}
              <div
                style={{
                  textAlign: "center",
                  marginBottom: "10px",
                  marginTop: "18px",
                }}
              >
                <div
                  style={{
                    fontSize: "2rem",
                    fontWeight: "200",
                    color: "rgba(255, 255, 255, 0.95)",
                    letterSpacing: "0.04em",
                    textShadow: ctSgdGlowStyle,
                    transition: "text-shadow 0.3s ease",
                  }}
                >
                  {ctFmtSGD(ctOrbVal)}
                </div>
              </div>

              {/* Chart */}
              <div
                style={{
                  position: "relative",
                  marginBottom: "32px",
                  userSelect: "none",
                }}
              >
                <svg
                  width="100%"
                  viewBox={ctViewBox}
                  style={{
                    overflow: "hidden",
                    display: "block",
                    cursor: ctChartCursor,
                  }}
                  onMouseMove={ctHandleChartMouse}
                  onTouchMove={ctHandleChartMouse}
                  onMouseDown={() => setIsDragging(true)}
                  onMouseUp={() => setIsDragging(false)}
                  onMouseLeave={() => setIsDragging(false)}
                  onTouchEnd={() => setIsDragging(false)}
                >
                  <defs>
                    <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor="rgba(255, 255, 255, 0.3)"
                        stopOpacity="1"
                      />
                      <stop
                        offset="50%"
                        stopColor="rgba(255, 255, 255, 0.1)"
                        stopOpacity="1"
                      />
                      <stop
                        offset="100%"
                        stopColor="rgba(255, 255, 255, 0)"
                        stopOpacity="1"
                      />
                    </linearGradient>
                    <linearGradient id="edgeFade" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="black" stopOpacity="0.55" />
                      <stop offset="8%" stopColor="black" stopOpacity="0" />
                      <stop offset="88%" stopColor="black" stopOpacity="0" />
                      <stop
                        offset="100%"
                        stopColor="black"
                        stopOpacity="0.75"
                      />
                    </linearGradient>
                    <mask id="edgeMask">
                      <rect x="0" y="0" width={CW} height={CH} fill="white" />
                      <rect
                        x="0"
                        y="0"
                        width={CW}
                        height={CH}
                        fill="url(#edgeFade)"
                      />
                    </mask>
                  </defs>
                  {/* Area fill — masked so left/right edges fade to transparent, no hard border */}
                  <path
                    d={ctAreaD}
                    fill="url(#areaFill)"
                    mask="url(#edgeMask)"
                  />

                  {/* Single glowing line */}
                  <g
                    style={{
                      filter:
                        "drop-shadow(0 0 4px rgba(255, 255, 255, 0.9)) drop-shadow(0 0 10px rgba(236, 228, 255, 0.55))",
                    }}
                  >
                    <path
                      d={ctPathD}
                      fill="none"
                      stroke="rgba(255, 255, 255, 0.95)"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>

                  {/* Orb vertical guide */}
                  <line
                    x1={ctOrbX}
                    y1={ctOrbY + 6}
                    x2={ctOrbX}
                    y2={CH}
                    stroke="rgba(255, 255, 255, 0.14)"
                    strokeWidth="0.5"
                    strokeDasharray="2 5"
                  />

                  {/* Large invisible hit area for easier touch/click */}
                  <circle
                    cx={ctOrbX}
                    cy={ctOrbY}
                    r="28"
                    fill="transparent"
                    style={{ cursor: "grab" }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      setIsDragging(true);
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      setIsDragging(true);
                    }}
                  />
                  {/* Orb — no solid fill, pure diffuse glow like manifest multiplier */}
                  <circle
                    className={isDragging ? "chart-orb-dragging" : "chart-orb"}
                    cx={ctOrbX}
                    cy={ctOrbY}
                    r={isDragging ? 5 : 4}
                    fill="rgba(255, 255, 255, 0.96)"
                    style={{ cursor: "grab" }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      setIsDragging(true);
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      setIsDragging(true);
                    }}
                  />
                </svg>

                {/* Info pill — positioned below orb using absolute % */}
                <div
                  style={{
                    position: "absolute",
                    left: (ctOrbX / CW) * 100 + "%",
                    bottom: "-26px",
                    transform: "translateX(-50%)",
                    background: "rgba(255, 255, 255, 0.14)",
                    border: "1px solid rgba(255, 255, 255, 0.28)",
                    borderRadius: "20px",
                    padding: "3px 10px",
                    pointerEvents: "none",
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                    whiteSpace: "nowrap",
                    display: "flex",
                    gap: "6px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "0.65rem",
                      fontWeight: "500",
                      color: "#86f7dc",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {ctMonthLabel(ctClampedOrb)}
                  </span>
                  <span
                    style={{
                      fontSize: "0.6rem",
                      color: "rgba(208, 198, 244, 0.5)",
                    }}
                  >
                    ·
                  </span>
                  <span
                    style={{
                      fontSize: "0.6rem",
                      color: "rgba(208, 198, 244, 0.55)",
                      letterSpacing: "0.02em",
                    }}
                  >
                    {ctOrbDistLabel}
                  </span>
                </div>
              </div>

              {/* Axis */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "0 4px",
                  marginBottom: "12px",
                }}
              >
                {ctAxisLabels.map((m) => (
                  <span
                    key={m}
                    style={{
                      fontSize: "0.65rem",
                      color: "rgba(208, 198, 244, 0.45)",
                      letterSpacing: "0.03em",
                    }}
                  >
                    {m === 0 ? "Now" : ctMonthLabel(m)}
                  </span>
                ))}
              </div>

              {/* Year toggle */}
              <div
                style={{
                  display: "flex",
                  gap: "6px",
                  justifyContent: "center",
                }}
              >
                {[1, 3, 5, 10].map((y) => (
                  <button
                    key={y}
                    onClick={() => {
                      setProjectionYears(y);
                      setOrbMonth(Math.min(orbMonth, y * 12));
                    }}
                    style={{
                      padding: "4px 14px",
                      fontSize: "0.75rem",
                      borderRadius: "8px",
                      border: "1px solid rgba(255, 255, 255, 0.16)",
                      background:
                        projectionYears === y
                          ? "linear-gradient(135deg,#86f7dc 0%,#a9fbe9 100%)"
                          : "rgba(255, 255, 255, 0.15)",
                      color: projectionYears === y ? "#06301f" : "rgba(236,228,255,0.82)",
                      cursor: "pointer",
                      letterSpacing: "0.03em",
                    }}
                  >
                    {y}y
                  </button>
                ))}
              </div>

              {/* Create tab quote — independent from tab 1 quote */}
              <div style={{ marginTop: "20px" }}>
                {!isEditingCreateQuote ? (
                  <div
                    onClick={() => setIsEditingCreateQuote(true)}
                    style={{
                      padding: "18px 32px",
                      cursor: "pointer",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%,-50%)",
                        width: "90%",
                        height: "95%",
                        background:
                          "radial-gradient(ellipse at center,rgba(255, 255, 255, 0.09) 0%,rgba(255, 255, 255, 0.2) 40%,transparent 70%)",
                        filter: "blur(20px)",
                        zIndex: 0,
                        pointerEvents: "none",
                        opacity: 0.25,
                      }}
                    />
                    <p
                      style={{
                        fontSize: "1.02rem",
                        color: "#ffffff",
                        fontStyle: "italic",
                        textAlign: "center",
                        margin: 0,
                        lineHeight: "1.7",
                        fontWeight: "400",
                        textShadow: "0 0 12px rgba(255, 255, 255, 0.3)",
                        position: "relative",
                        zIndex: 1,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {'"' +
                        (createQuote || "Tap to add a quote...").trimEnd() +
                        '"'}
                    </p>
                  </div>
                ) : (
                  <div>
                    <textarea
                      value={createQuote}
                      onChange={(e) => setCreateQuote(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "16px",
                        background: "rgba(14, 8, 60, 0.34)",
                        border: "1px solid rgba(255, 255, 255, 0.16)",
                        borderRadius: "12px",
                        fontSize: "1rem",
                        color: "rgba(236, 228, 255, 0.82)",
                        fontStyle: "italic",
                        resize: "vertical",
                        minHeight: "80px",
                        fontFamily: "inherit",
                        boxSizing: "border-box",
                        whiteSpace: "pre-wrap",
                      }}
                      placeholder="Enter your inspiring quote..."
                    />
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        marginTop: "10px",
                        justifyContent: "flex-end",
                      }}
                    >
                      <button
                        onClick={() => {
                          setCreateQuote(
                            localStorage.getItem("create-quote") || ""
                          );
                          setIsEditingCreateQuote(false);
                        }}
                        style={{
                          padding: "8px 16px",
                          background: "rgba(255, 255, 255, 0.1)",
                          color: "rgba(236, 228, 255, 0.82)",
                          border: "1px solid rgba(255, 255, 255, 0.16)",
                          borderRadius: "9px",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          localStorage.setItem("create-quote", createQuote);
                          setIsEditingCreateQuote(false);
                        }}
                        style={{
                          padding: "8px 16px",
                          background:
                            "linear-gradient(135deg,#86f7dc 0%,#a9fbe9 100%)",
                          color: "#06301f",
                          border: "none",
                          borderRadius: "9px",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                        }}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ── Panel 2: Revenue Streams ── */}
            <div style={pnl}>
              <h2
                style={{
                  fontSize: "1.05rem",
                  fontWeight: "400",
                  color: "#ffffff",
                  marginBottom: "16px",
                  letterSpacing: "0.3px",
                }}
              >
                Revenue Streams
              </h2>

              {/* Summary cards — same style as total/crypto/fiat panels */}
              <div
                className="grid-2"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2,1fr)",
                  gap: "14px",
                  marginBottom: "20px",
                }}
              >
                <div
                  style={{
                    background: "rgba(255, 255, 255, 0.075)",
                    borderRadius: "18px",
                    padding: "20px",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    boxShadow:
                      "0 3px 12px rgba(8, 4, 40, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.25)",
                  }}
                >
                  <p
                    style={{
                      fontSize: "0.68rem",
                      color: "rgba(208, 198, 244, 0.68)",
                      fontWeight: "500",
                      letterSpacing: "0.8px",
                      textTransform: "uppercase",
                      margin: "0 0 8px 0",
                    }}
                  >
                    Monthly recurring
                  </p>
                  <p
                    style={{
                      fontSize: "1.45rem",
                      fontWeight: "400",
                      color: "#ffffff",
                      margin: 0,
                      textShadow: "0 1px 6px rgba(255, 255, 255, 0.45)",
                    }}
                  >
                    {ctFmtSGD(ctTotalMonthlyRecurring)}
                  </p>
                </div>
                <div
                  style={{
                    background: "rgba(255, 255, 255, 0.075)",
                    borderRadius: "18px",
                    padding: "20px",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    boxShadow:
                      "0 3px 12px rgba(8, 4, 40, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.25)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      margin: "0 0 8px 0",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "0.68rem",
                        color: "rgba(208, 198, 244, 0.68)",
                        fontWeight: "500",
                        letterSpacing: "0.8px",
                        textTransform: "uppercase",
                        margin: 0,
                      }}
                    >
                      Total inflow
                    </p>
                    <div style={{ display: "flex", gap: "3px" }}>
                      {[1, 3, 5, 10].map((y) => (
                        <button
                          key={y}
                          onClick={() => {
                            setProjectionYears(y);
                            setOrbMonth(Math.min(orbMonth, y * 12));
                          }}
                          style={{
                            padding: "2px 6px",
                            fontSize: "0.58rem",
                            borderRadius: "5px",
                            border: "none",
                            background:
                              projectionYears === y
                                ? "rgba(134, 247, 220, 0.88)"
                                : "rgba(255, 255, 255, 0.18)",
                            color:
                              projectionYears === y
                                ? "#06301f"
                                : "rgba(208, 198, 244, 0.6)",
                            cursor: "pointer",
                            fontWeight: projectionYears === y ? "500" : "400",
                          }}
                        >
                          {y}y
                        </button>
                      ))}
                    </div>
                  </div>
                  <p
                    style={{
                      fontSize: "1.45rem",
                      fontWeight: "400",
                      color: "#ffffff",
                      margin: 0,
                      textShadow: "0 1px 6px rgba(255, 255, 255, 0.45)",
                    }}
                  >
                    {ctFmtSGD(ctTotalInflow)}
                  </p>
                </div>
              </div>

              {/* Stream rows */}
              {revenueStreams.map((s) =>
                editingStreamId === s.id ? null : (
                  <div
                    key={s.id}
                    style={{
                      padding: "11px 14px",
                      borderRadius: "10px",
                      background: "rgba(255, 255, 255, 0.15)",
                      border: "1px solid rgba(255, 255, 255, 0.22)",
                      marginBottom: "7px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "6px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.82rem",
                          fontWeight: "500",
                          color: "#ffffff",
                          flex: 1,
                          marginRight: "8px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {s.name}
                      </span>
                      <div
                        style={{ display: "flex", gap: "5px", flexShrink: 0 }}
                      >
                        <button
                          onClick={() => ctStartEdit(s)}
                          style={{
                            width: "22px",
                            height: "22px",
                            borderRadius: "5px",
                            background: "rgba(255, 255, 255, 0.12)",
                            border: "1px solid rgba(255, 255, 255, 0.18)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            fontSize: "0.65rem",
                            color: "rgba(208, 198, 244, 0.6)",
                          }}
                        >
                          ✎
                        </button>
                        <button
                          onClick={() => ctDeleteStream(s.id)}
                          style={{
                            width: "22px",
                            height: "22px",
                            borderRadius: "5px",
                            background: "rgba(255, 180, 200, 0.15)",
                            border: "1px solid rgba(255, 180, 200, 0.2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            fontSize: "0.65rem",
                            color: "#ffb4c8",
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px 14px",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.75rem",
                          color: "#ffffff",
                          fontWeight: "500",
                        }}
                      >
                        SGD {(parseFloat(s.amount) || 0).toLocaleString()} /{" "}
                        {s.amountUnit || "mo"}
                      </span>
                      {s.startDate && (
                        <span
                          style={{
                            fontSize: "0.7rem",
                            color: "rgba(255, 255, 255, 0.82)",
                          }}
                        >
                          {new Date(s.startDate).toLocaleDateString("en-GB", {
                            month: "short",
                            year: "2-digit",
                          })}
                        </span>
                      )}
                      {s.type === "one-time" ? (
                        <span
                          style={{
                            fontSize: "0.65rem",
                            padding: "2px 7px",
                            borderRadius: "5px",
                            background: "rgba(196, 176, 255, 0.28)",
                            color: "#ffffff",
                            fontWeight: "500",
                          }}
                        >
                          one-time
                        </span>
                      ) : s.duration ? (
                        <span
                          style={{
                            fontSize: "0.7rem",
                            color: "rgba(255, 255, 255, 0.82)",
                          }}
                        >
                          {s.duration} {s.durationUnit}
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: "0.65rem",
                            padding: "2px 7px",
                            borderRadius: "5px",
                            background: "rgba(255, 255, 255, 0.06)",
                            color: "#ffffff",
                            fontWeight: "500",
                          }}
                        >
                          ongoing
                        </span>
                      )}
                      {s.compound && (
                        <span
                          style={{
                            fontSize: "0.7rem",
                            color: "rgba(255, 255, 255, 0.82)",
                          }}
                        >
                          {s.compound}% / yr
                        </span>
                      )}
                    </div>
                  </div>
                )
              )}

              {/* Inline add / edit form */}
              {showAddStream && (
                <div
                  style={{
                    padding: "14px",
                    borderRadius: "12px",
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    marginBottom: "10px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                      marginBottom: "12px",
                    }}
                  >
                    {/* Row 1: Revenue Stream name */}
                    <div>
                      <div
                        style={{
                          fontSize: "0.62rem",
                          color: "rgba(208, 198, 244, 0.55)",
                          textTransform: "uppercase",
                          letterSpacing: "0.07em",
                          marginBottom: "4px",
                        }}
                      >
                        Revenue Stream
                      </div>
                      <input
                        value={streamForm.name}
                        onChange={(e) =>
                          setStreamForm((f) => ({ ...f, name: e.target.value }))
                        }
                        placeholder="e.g. Main salary"
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          background: "rgba(14, 8, 60, 0.34)",
                          border: "1px solid rgba(255, 255, 255, 0.16)",
                          borderRadius: "8px",
                          fontSize: "0.85rem",
                          color: "rgba(236, 228, 255, 0.82)",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                    {/* Row 2: Amount + per mo/yr unit toggle */}
                    <div>
                      <div
                        style={{
                          fontSize: "0.62rem",
                          color: "rgba(208, 198, 244, 0.55)",
                          textTransform: "uppercase",
                          letterSpacing: "0.07em",
                          marginBottom: "4px",
                        }}
                      >
                        Amount (SGD)
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",
                        }}
                      >
                        <input
                          value={streamForm.amount}
                          onChange={(e) =>
                            setStreamForm((f) => ({
                              ...f,
                              amount: e.target.value,
                            }))
                          }
                          placeholder="0"
                          type="text"
                          inputMode="decimal"
                          onWheel={(e) => e.target.blur()}
                          style={{
                            flex: 1,
                            padding: "8px 12px",
                            background: "rgba(14, 8, 60, 0.34)",
                            border: "1px solid rgba(255, 255, 255, 0.16)",
                            borderRadius: "8px",
                            fontSize: "0.85rem",
                            color: "rgba(236, 228, 255, 0.82)",
                          }}
                        />
                        <div
                          style={{ display: "flex", gap: "4px", flexShrink: 0 }}
                        >
                          {["mo", "yr"].map((u) => (
                            <button
                              key={u}
                              onClick={() =>
                                setStreamForm((f) => ({ ...f, amountUnit: u }))
                              }
                              style={{
                                padding: "6px 10px",
                                fontSize: "0.7rem",
                                borderRadius: "8px",
                                border: "1px solid rgba(255, 255, 255, 0.16)",
                                background:
                                  (streamForm.amountUnit || "mo") === u
                                    ? "linear-gradient(135deg,#86f7dc,#a9fbe9)"
                                    : "rgba(255, 255, 255, 0.25)",
                                color:
                                  (streamForm.amountUnit || "mo") === u
                                    ? "white"
                                    : "rgba(236, 228, 255, 0.82)",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                              }}
                            >
                              / {u}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    {/* Row 3: Start date + Type buttons — each in own half */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "10px",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: "0.62rem",
                            color: "rgba(208, 198, 244, 0.55)",
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                            marginBottom: "4px",
                          }}
                        >
                          Start date
                        </div>
                        <input
                          value={streamForm.startDate}
                          onChange={(e) =>
                            setStreamForm((f) => ({
                              ...f,
                              startDate: e.target.value,
                            }))
                          }
                          type="month"
                          style={{
                            width: "100%",
                            padding: "8px 8px",
                            background: "rgba(14, 8, 60, 0.34)",
                            border: "1px solid rgba(255, 255, 255, 0.16)",
                            borderRadius: "8px",
                            fontSize: "0.78rem",
                            color: "rgba(236, 228, 255, 0.82)",
                          }}
                        />
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: "0.62rem",
                            color: "rgba(208, 198, 244, 0.55)",
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                            marginBottom: "4px",
                          }}
                        >
                          Type
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "5px",
                            height: "36px",
                          }}
                        >
                          {[
                            ["recurring", "Recurring"],
                            ["one-time", "One-Time"],
                          ].map(([val, label]) => (
                            <button
                              key={val}
                              onClick={() =>
                                setStreamForm((f) => ({ ...f, type: val }))
                              }
                              style={{
                                flex: 1,
                                padding: "0",
                                fontSize: "0.7rem",
                                borderRadius: "8px",
                                border: "1px solid rgba(255, 255, 255, 0.16)",
                                background:
                                  streamForm.type === val
                                    ? "linear-gradient(135deg,#86f7dc,#a9fbe9)"
                                    : "rgba(255, 255, 255, 0.25)",
                                color:
                                  streamForm.type === val ? "#06301f" : "rgba(236,228,255,0.82)",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                              }}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    {/* Row 4: Duration (recurring only) + Compound */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          streamForm.type === "recurring" ? "1fr 1fr" : "1fr",
                        gap: "10px",
                      }}
                    >
                      {streamForm.type === "recurring" && (
                        <div>
                          <div
                            style={{
                              fontSize: "0.62rem",
                              color: "rgba(208, 198, 244, 0.55)",
                              textTransform: "uppercase",
                              letterSpacing: "0.07em",
                              marginBottom: "4px",
                            }}
                          >
                            Duration
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: "5px",
                              alignItems: "center",
                            }}
                          >
                            <input
                              value={streamForm.duration}
                              onChange={(e) =>
                                setStreamForm((f) => ({
                                  ...f,
                                  duration: e.target.value,
                                }))
                              }
                              placeholder="ongoing"
                              type="text"
                              inputMode="decimal"
                              onWheel={(e) => e.target.blur()}
                              style={{
                                flex: 1,
                                minWidth: 0,
                                padding: "8px 8px",
                                background: "rgba(14, 8, 60, 0.34)",
                                border: "1px solid rgba(255, 255, 255, 0.16)",
                                borderRadius: "8px",
                                fontSize: "0.78rem",
                                color: "rgba(236, 228, 255, 0.82)",
                              }}
                            />
                            <div
                              style={{
                                display: "flex",
                                gap: "3px",
                                flexShrink: 0,
                              }}
                            >
                              {["mo", "yr"].map((u) => (
                                <button
                                  key={u}
                                  onClick={() =>
                                    setStreamForm((f) => ({
                                      ...f,
                                      durationUnit: u,
                                    }))
                                  }
                                  style={{
                                    padding: "4px 6px",
                                    fontSize: "0.6rem",
                                    borderRadius: "5px",
                                    border: "none",
                                    background:
                                      streamForm.durationUnit === u
                                        ? "rgba(134, 247, 220, 0.88)"
                                        : "rgba(255, 255, 255, 0.18)",
                                    color:
                                      streamForm.durationUnit === u
                                        ? "#06301f"
                                        : "rgba(208, 198, 244, 0.6)",
                                    cursor: "pointer",
                                    fontWeight:
                                      streamForm.durationUnit === u
                                        ? "500"
                                        : "400",
                                  }}
                                >
                                  {u}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      <div>
                        <div
                          style={{
                            fontSize: "0.62rem",
                            color: "rgba(208, 198, 244, 0.55)",
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                            marginBottom: "4px",
                          }}
                        >
                          Compound % / yr
                        </div>
                        <input
                          value={streamForm.compound}
                          onChange={(e) =>
                            setStreamForm((f) => ({
                              ...f,
                              compound: e.target.value,
                            }))
                          }
                          placeholder="e.g. 5  (blank = flat)"
                          type="text"
                          inputMode="decimal"
                          onWheel={(e) => e.target.blur()}
                          style={{
                            width: "100%",
                            padding: "8px 8px",
                            background: "rgba(14, 8, 60, 0.34)",
                            border: "1px solid rgba(255, 255, 255, 0.16)",
                            borderRadius: "8px",
                            fontSize: "0.78rem",
                            color: "rgba(236, 228, 255, 0.82)",
                            boxSizing: "border-box",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={ctSaveStream}
                      disabled={!streamForm.name || !streamForm.amount}
                      style={{
                        flex: 1,
                        padding: "9px",
                        background:
                          "linear-gradient(135deg,#86f7dc 0%,#a9fbe9 100%)",
                        color: "#06301f",
                        border: "none",
                        borderRadius: "9px",
                        cursor: "pointer",
                        fontSize: "0.8rem",
                        fontWeight: "500",
                        opacity:
                          !streamForm.name || !streamForm.amount ? 0.4 : 1,
                      }}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setShowAddStream(false);
                        setEditingStreamId(null);
                        setStreamForm(blankStream);
                      }}
                      style={{
                        flex: 1,
                        padding: "9px",
                        background: "rgba(255, 255, 255, 0.1)",
                        color: "rgba(236, 228, 255, 0.82)",
                        border: "1px solid rgba(255, 255, 255, 0.16)",
                        borderRadius: "9px",
                        cursor: "pointer",
                        fontSize: "0.8rem",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {!showAddStream && (
                <button
                  onClick={() => {
                    setStreamForm(blankStream);
                    setEditingStreamId(null);
                    setShowAddStream(true);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    width: "100%",
                    height: "34px",
                    borderRadius: "10px",
                    background: "rgba(255, 255, 255, 0.08)",
                    border: "1px dashed rgba(255, 255, 255, 0.12)",
                    fontSize: "0.78rem",
                    color: "rgba(208, 198, 244, 0.55)",
                    cursor: "pointer",
                    letterSpacing: "0.03em",
                  }}
                >
                  + add revenue stream
                </button>
              )}
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════════════════
            TAB: HEART  ♡  — Flower of Life + Acts of Love
        ════════════════════════════════════════════════════════ */}
        {activeTab === "charts" && (
          <>
            <div style={pnl}>
              {/* Quote */}
              <div style={{ marginBottom: "4px" }}>
                {!isEditingHeartQuote ? (
                  <div
                    onClick={() => setIsEditingHeartQuote(true)}
                    style={{
                      padding: "10px 32px",
                      cursor: "pointer",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%,-50%)",
                        width: "90%",
                        height: "95%",
                        background:
                          "radial-gradient(ellipse at center,rgba(255, 255, 255, 0.09) 0%,rgba(255, 255, 255, 0.2) 40%,transparent 70%)",
                        filter: "blur(20px)",
                        zIndex: 0,
                        pointerEvents: "none",
                        opacity: 0.25,
                      }}
                    />
                    <p
                      style={{
                        fontSize: "1.02rem",
                        color: "#ffffff",
                        fontStyle: "italic",
                        textAlign: "center",
                        margin: 0,
                        lineHeight: "1.7",
                        fontWeight: "400",
                        textShadow: "0 0 12px rgba(255, 255, 255, 0.3)",
                        position: "relative",
                        zIndex: 1,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {'"' + (heartQuote || "").trimEnd() + '"'}
                    </p>
                  </div>
                ) : (
                  <div>
                    <textarea
                      value={heartQuote}
                      onChange={(e) => setHeartQuote(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "16px",
                        background: "rgba(14, 8, 60, 0.34)",
                        border: "1px solid rgba(255, 255, 255, 0.16)",
                        borderRadius: "12px",
                        fontSize: "1rem",
                        color: "rgba(236, 228, 255, 0.82)",
                        fontStyle: "italic",
                        resize: "vertical",
                        minHeight: "80px",
                        fontFamily: "inherit",
                        boxSizing: "border-box",
                      }}
                      placeholder="Enter your quote..."
                    />
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        marginTop: "10px",
                        justifyContent: "flex-end",
                      }}
                    >
                      <button
                        onClick={() => {
                          setHeartQuote(
                            localStorage.getItem("heart-quote") ||
                              "The love you give is the love you receive."
                          );
                          setIsEditingHeartQuote(false);
                        }}
                        style={{
                          padding: "8px 16px",
                          background: "rgba(255, 255, 255, 0.1)",
                          color: "rgba(236, 228, 255, 0.82)",
                          border: "1px solid rgba(255, 255, 255, 0.16)",
                          borderRadius: "9px",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          localStorage.setItem("heart-quote", heartQuote);
                          setIsEditingHeartQuote(false);
                        }}
                        style={{
                          padding: "8px 16px",
                          background:
                            "linear-gradient(135deg,#86f7dc 0%,#a9fbe9 100%)",
                          color: "#06301f",
                          border: "none",
                          borderRadius: "9px",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                        }}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Flower of Life geometry */}
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  paddingTop: "130%",
                  marginBottom: "0px",
                  marginTop: "0px",
                }}
              >
                <svg
                  className="flower-glow"
                  style={{
                    position: "absolute",
                    inset: "0",
                    width: "100%",
                    height: "100%",
                    overflow: "visible",
                  }}
                  viewBox="0 0 400 400"
                  preserveAspectRatio="xMidYMid meet"
                >
                  <defs>
                    <filter
                      id="flg"
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feGaussianBlur stdDeviation="1.5" result="b" />
                      <feMerge>
                        <feMergeNode in="b" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                    <radialGradient id="flowerCenterGlow">
                      <stop offset="0%" stopColor="rgba(255, 255, 255, 0.18)" />
                      <stop offset="40%" stopColor="rgba(255, 255, 255, 0.06)" />
                      <stop offset="100%" stopColor="rgba(255, 255, 255, 0)" />
                    </radialGradient>
                    {/* Glass fill for each circle — radial gradient from centre outward */}
                    <radialGradient id="glassFill">
                      <stop offset="0%" stopColor="rgba(255, 255, 255, 0.12)" />
                      <stop offset="60%" stopColor="rgba(255, 255, 255, 0.04)" />
                      <stop offset="100%" stopColor="rgba(255, 255, 255, 0)" />
                    </radialGradient>
                  </defs>
                  {(() => {
                    const cx = 200,
                      cy = 200;
                    const r = 40;
                    // Step 0 = just a dot (no entries). Steps 1-6 = rings. Step 7 = complete with boundary.
                    const step = flowerConverging
                      ? 7
                      : Math.min(actsOfLove.length, 7);

                    // The Flower of Life needs circles whose centres extend beyond the boundary
                    // so their arcs create petal patterns when clipped.

                    const allCenters = [];
                    const seen = new Set();
                    const dy = (r * Math.sqrt(3)) / 2;

                    // Generate hex grid — reduced range for performance
                    for (let row = -8; row <= 8; row++) {
                      for (let col = -8; col <= 8; col++) {
                        const x =
                          cx + col * r + (Math.abs(row) % 2 !== 0 ? r / 2 : 0);
                        const y = cy + row * dy;
                        const dist = Math.sqrt(
                          (x - cx) * (x - cx) + (y - cy) * (y - cy)
                        );
                        if (dist <= r * 4.5) {
                          const key =
                            Math.round(x * 10) + "," + Math.round(y * 10);
                          if (!seen.has(key)) {
                            seen.add(key);
                            allCenters.push({ x, y, dist });
                          }
                        }
                      }
                    }

                    // Sort clockwise from top
                    const getAngle = (x, y) => {
                      const a = (Math.atan2(y - cy, x - cx) * 180) / Math.PI;
                      return (((a + 90) % 360) + 360) % 360;
                    };

                    // Assign to 6 rings by distance from centre
                    // Hex grid distances: 0, r, r√3≈1.73r, 2r, r√7≈2.65r, 3r, 2r√3≈3.46r, 4r
                    const rings = [[], [], [], [], [], []];
                    for (const c of allCenters) {
                      let ri;
                      if (c.dist < 0.5) ri = 0;
                      else if (c.dist < r * 1.05) ri = 1; // dist = r
                      else if (c.dist < r * 2.05) ri = 2; // dist = r√3, 2r
                      else if (c.dist < r * 3.05) ri = 3; // dist = r√7, 3r
                      else if (c.dist < r * 3.8) ri = 4; // dist = 2r√3 ≈ 3.46r
                      else ri = 5; // dist = 4r+
                      rings[ri].push(c);
                    }

                    // Sort each ring clockwise
                    for (let i = 1; i < rings.length; i++) {
                      rings[i].sort(
                        (a, b) => getAngle(a.x, a.y) - getAngle(b.x, b.y)
                      );
                    }

                    const els = [];

                    // Radial glow that grows with each ring — subtle, not overpowering
                    const glowRadius =
                      step === 0 ? r * 0.5 : r * (1 + step * 0.6);
                    els.push(
                      <circle
                        key="cg"
                        cx={cx}
                        cy={cy}
                        r={glowRadius}
                        fill="url(#flowerCenterGlow)"
                        style={{
                          opacity: flowerConverging ? 0 : 1,
                          transition: "opacity 1s",
                        }}
                      />
                    );
                    // Secondary soft atmospheric glow — very subtle
                    if (step >= 2 && !flowerConverging) {
                      els.push(
                        <circle
                          key="cg2"
                          cx={cx}
                          cy={cy}
                          r={glowRadius * 0.5}
                          fill="rgba(255, 255, 255, 0.03)"
                        />
                      );
                    }

                    // Step 0: just a central glowing dot, no circles
                    if (step === 0) {
                      els.push(
                        <circle
                          key="seed-dot"
                          cx={cx}
                          cy={cy}
                          r={3}
                          fill="rgba(255, 255, 255, 0.85)"
                          filter="url(#flg)"
                        />
                      );
                      return els;
                    }

                    // Clip boundary
                    const clipR = r * 4;
                    const useClip = step >= 7; // keep clip during convergence too

                    // Build all visible circles with glass fill
                    const circleEls = [];
                    for (let s = 0; s < Math.min(step, 6); s++) {
                      const ringData = rings[s] || [];
                      const isNewest =
                        s === step - 1 &&
                        actsOfLove.length > 0 &&
                        !flowerConverging;
                      ringData.forEach((c, idx) => {
                        const strokeOp = s <= 1 ? 0.42 : s <= 3 ? 0.35 : 0.28;
                        // Glass fill — subtle translucency, brighter for inner circles
                        const fillOp = s <= 1 ? 0.04 : s <= 3 ? 0.025 : 0.015;
                        const fillColor = `rgba(255,255,255,${fillOp})`;
                        if (flowerConverging) {
                          const delay = (c.dist / (r * 4)) * 1.2;
                          circleEls.push(
                            <circle
                              key={`fl-${s}-${idx}`}
                              cx={c.x}
                              cy={c.y}
                              r={r}
                              stroke={`rgba(255,255,255,${strokeOp})`}
                              strokeWidth="1.2"
                              fill={fillColor}
                              filter="url(#flg)"
                              className="flower-converge"
                              style={{ animationDelay: delay + "s" }}
                            />
                          );
                        } else if (isNewest) {
                          circleEls.push(
                            <circle
                              key={`fl-${s}-${idx}`}
                              cx={c.x}
                              cy={c.y}
                              r={r}
                              stroke={`rgba(255,255,255,${strokeOp})`}
                              strokeWidth="1.2"
                              fill={fillColor}
                              filter="url(#flg)"
                              className="flower-ring-enter"
                              style={{
                                animationDelay: idx * 0.15 + "s",
                                opacity: 0,
                              }}
                            />
                          );
                        } else {
                          circleEls.push(
                            <circle
                              key={`fl-${s}-${idx}`}
                              cx={c.x}
                              cy={c.y}
                              r={r}
                              stroke={`rgba(255,255,255,${strokeOp})`}
                              strokeWidth="1.2"
                              fill={fillColor}
                              filter="url(#flg)"
                            />
                          );
                        }
                      });
                    }

                    // Glowing dots at circle centres AND intersection points
                    // Skip entirely during convergence animation
                    // Skip when fewer than 2 rings are fully revealed (prevents floating centre dot at step 2)
                    const dotEls = [];
                    const allVisibleCenters = [];
                    if (!flowerConverging && step >= 3) {
                      for (let s = 0; s < Math.min(step, 6); s++) {
                        const isNewest =
                          s === step - 1 && actsOfLove.length > 0;
                        if (isNewest) continue;
                        (rings[s] || []).forEach((c) => {
                          allVisibleCenters.push(c);
                        });
                      }
                    }

                    // Compute intersection points between all pairs of visible circles
                    const dotSeen = new Set();
                    const addDot = (x, y) => {
                      const key = Math.round(x * 5) + "," + Math.round(y * 5);
                      if (dotSeen.has(key)) return;
                      dotSeen.add(key);
                      const dist = Math.sqrt(
                        (x - cx) * (x - cx) + (y - cy) * (y - cy)
                      );
                      if (dist > r * 4.6) return;
                      const idx = dotSeen.size;
                      dotEls.push(
                        <circle
                          key={`dt${idx}`}
                          cx={x}
                          cy={y}
                          r={1.8}
                          fill="rgba(255, 255, 255, 0.6)"
                          filter="url(#flg)"
                        />
                      );
                    };

                    // Add dots at all visible circle centres
                    for (const c of allVisibleCenters) {
                      addDot(c.x, c.y);
                    }

                    // Add dots at intersection points — only check nearby pairs (distance < 2r)
                    // Use spatial bucketing for O(n) instead of O(n²)
                    const bucketSize = r * 2.1;
                    const buckets = {};
                    for (const c of allVisibleCenters) {
                      const bx = Math.floor(c.x / bucketSize);
                      const by = Math.floor(c.y / bucketSize);
                      const key = bx + "," + by;
                      if (!buckets[key]) buckets[key] = [];
                      buckets[key].push(c);
                    }
                    for (const c of allVisibleCenters) {
                      const bx = Math.floor(c.x / bucketSize);
                      const by = Math.floor(c.y / bucketSize);
                      for (let dbx = -1; dbx <= 1; dbx++) {
                        for (let dby = -1; dby <= 1; dby++) {
                          const neighbours =
                            buckets[bx + dbx + "," + (by + dby)];
                          if (!neighbours) continue;
                          for (const n of neighbours) {
                            if (n === c) continue;
                            const dx = n.x - c.x,
                              ddy = n.y - c.y;
                            const d = Math.sqrt(dx * dx + ddy * ddy);
                            if (d > 0.1 && d < r * 2 - 0.1) {
                              const aa = d / 2;
                              const h = Math.sqrt(r * r - aa * aa);
                              const mx = (c.x + n.x) / 2,
                                my = (c.y + n.y) / 2;
                              const px = (-ddy / d) * h,
                                py = (dx / d) * h;
                              addDot(mx + px, my + py);
                              addDot(mx - px, my - py);
                            }
                          }
                        }
                      }
                    }

                    // At step 7: clip everything + boundary + radial pulse beyond boundary
                    if (useClip) {
                      els.push(
                        <defs key="clipdef">
                          <clipPath id="flowerClip">
                            <circle cx={cx} cy={cy} r={clipR} />
                          </clipPath>
                        </defs>
                      );
                      els.push(
                        <g key="clipped" clipPath="url(#flowerClip)">
                          {circleEls}
                          {dotEls}
                        </g>
                      );
                      // Pulsating radial glow — OUTSIDE clip so it spreads beyond boundary circles
                      {
                        !flowerConverging &&
                          els.push(
                            <g key="radial-pulses">
                              <circle
                                cx={cx}
                                cy={cy}
                                r={0}
                                fill="none"
                                stroke="rgba(255, 255, 255, 0.2)"
                                strokeWidth="14"
                                className="flower-radial-pulse"
                                filter="url(#flg)"
                              />
                              <circle
                                cx={cx}
                                cy={cy}
                                r={0}
                                fill="none"
                                stroke="rgba(255, 255, 255, 0.14)"
                                strokeWidth="10"
                                className="flower-radial-pulse"
                                filter="url(#flg)"
                                style={{ animationDelay: "1.5s" }}
                              />
                              <circle
                                cx={cx}
                                cy={cy}
                                r={0}
                                fill="none"
                                stroke="rgba(255, 255, 255, 0.08)"
                                strokeWidth="8"
                                className="flower-radial-pulse"
                                filter="url(#flg)"
                                style={{ animationDelay: "3s" }}
                              />
                            </g>
                          );
                      }
                      // Boundary circles — fade out during convergence, fade in normally
                      const boundaryStyle = flowerConverging
                        ? { animation: "flowerFadeOut 2s ease-in forwards" }
                        : {
                            animation: "flowerFadeIn 2s ease-in forwards",
                            opacity: 0,
                          };
                      els.push(
                        <circle
                          key="ob1"
                          cx={cx}
                          cy={cy}
                          r={clipR}
                          stroke="rgba(255, 255, 255, 0.55)"
                          strokeWidth="2.5"
                          fill="none"
                          filter="url(#flg)"
                          style={boundaryStyle}
                        />
                      );
                      els.push(
                        <circle
                          key="ob2"
                          cx={cx}
                          cy={cy}
                          r={clipR + 5}
                          stroke="rgba(255, 255, 255, 0.35)"
                          strokeWidth="1.8"
                          fill="none"
                          filter="url(#flg)"
                          style={boundaryStyle}
                        />
                      );
                    } else {
                      circleEls.forEach((c) => els.push(c));
                      dotEls.forEach((d) => els.push(d));
                    }

                    return els;
                  })()}
                </svg>
              </div>

              {/* Whisper field — bare glowing text input, no border */}
              {!flowerConverging && (
                <div
                  style={{
                    textAlign: "center",
                    marginBottom: "24px",
                    position: "relative",
                  }}
                >
                  {/* Glow overlay — shown after submit, fades out */}
                  {whisperGlowing && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        pointerEvents: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        className="whisper-orb"
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%,-50%)",
                          width: "200px",
                          height: "70px",
                          borderRadius: "50%",
                          background:
                            "radial-gradient(ellipse at center, rgba(255, 255, 255, 0.22) 0%, transparent 70%)",
                          pointerEvents: "none",
                        }}
                      />
                    </div>
                  )}
                  {/* Input always visible — double-tap on mobile clears and re-focuses */}
                  <input
                    ref={whisperInputRef}
                    className={
                      "whisper-field" + (whisperGlowing ? " whisper-glow" : "")
                    }
                    type="text"
                    autoCorrect="off"
                    autoCapitalize="sentences"
                    autoComplete="off"
                    spellCheck={false}
                    value={whisperText}
                    onChange={(e) => {
                      if (!whisperGlowing) setWhisperText(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        whisperText.trim() &&
                        actsOfLove.length < 7 &&
                        !whisperGlowing &&
                        !flowerConverging
                      ) {
                        const newAct = {
                          id: "a" + Date.now(),
                          action: whisperText.trim(),
                          amount: "",
                          date: new Date().toISOString(),
                        };
                        const updated = [...actsOfLove, newAct];
                        setActsOfLove(updated);
                        localStorage.setItem(
                          "acts-of-love",
                          JSON.stringify(updated)
                        );
                        whisperInputRef.current?.blur();
                        setWhisperGlowing(true);
                      }
                    }}
                    onDoubleClick={() => {
                      if (whisperGlowing) {
                        whisperInputRef.current?.focus();
                        setWhisperText("");
                        setWhisperGlowing(false);
                      }
                    }}
                    onTouchEnd={(e) => {
                      if (whisperGlowing) {
                        e.preventDefault();
                        whisperInputRef.current?.focus();
                        setWhisperText("");
                        setWhisperGlowing(false);
                      }
                    }}
                    placeholder="Giving Love"
                    style={{
                      background: "transparent",
                      border: "none",
                      outline: "none",
                      boxShadow: "none",
                      WebkitAppearance: "none",
                      appearance: "none",
                      width: "100%",
                      textAlign: "center",
                      fontStyle: "italic",
                      fontWeight: "300",
                      color: whisperGlowing
                        ? "rgba(255, 255, 255, 0.96)"
                        : "rgba(255, 255, 255, 0.8)",
                      letterSpacing: "0.04em",
                      padding: "8px 0",
                      fontFamily: "inherit",
                      transition: "color 0.4s ease",
                    }}
                  />
                </div>
              )}

              {/* Step progress indicator */}
              <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "6px",
                    marginBottom: "8px",
                  }}
                >
                  {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                    <div
                      key={i}
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        background:
                          i < Math.min(actsOfLove.length, 7)
                            ? "rgba(255, 255, 255, 0.96)"
                            : "rgba(255, 255, 255, 0.15)",
                        boxShadow:
                          i < Math.min(actsOfLove.length, 7)
                            ? "0 0 8px rgba(255, 255, 255, 0.85), 0 0 18px rgba(255, 255, 255, 0.4)"
                            : "none",
                        transition: "all 0.6s ease",
                      }}
                    />
                  ))}
                </div>
                <p
                  style={{
                    fontSize: "0.72rem",
                    color: "rgba(208, 198, 244, 0.5)",
                    letterSpacing: "0.05em",
                  }}
                >
                  {flowerConverging
                    ? "Planting a new seed..."
                    : actsOfLove.length === 0
                    ? "From seed to flower"
                    : actsOfLove.length >= 7
                    ? "Flowering to infinity"
                    : `Ring ${Math.min(actsOfLove.length, 7)} of 7`}
                </p>
                {actsOfLove.length >= 7 && !flowerConverging && (
                  <button
                    onClick={() => {
                      setWhisperText("");
                      setWhisperGlowing(false);
                      setFlowerConverging(true);
                      setTimeout(() => {
                        setActsOfLove([]);
                        localStorage.setItem(
                          "acts-of-love",
                          JSON.stringify([])
                        );
                        setFlowerConverging(false);
                      }, 3500);
                    }}
                    style={{
                      marginTop: "10px",
                      padding: "8px 20px",
                      background: "rgba(255, 255, 255, 0.15)",
                      color: "rgba(208, 198, 244, 0.6)",
                      border: "1px solid rgba(255, 255, 255, 0.25)",
                      borderRadius: "20px",
                      cursor: "pointer",
                      fontSize: "0.75rem",
                      letterSpacing: "0.05em",
                      fontStyle: "italic",
                    }}
                  >
                    Plant a new seed ✿
                  </button>
                )}
              </div>
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════════════════
            TAB: CALENDAR  🗓️
        ════════════════════════════════════════════════════════ */}
        {activeTab === "calendar" && (
          <>
            {/* Calendar */}
            <div style={{ ...pnl, marginBottom: "14px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginBottom: "16px",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <div
                  style={{ display: "flex", gap: "16px", alignItems: "center" }}
                >
                  <button
                    onClick={() => changeCalendarMonth(-1)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#ffffff",
                      fontSize: "1.55rem",
                      fontWeight: "300",
                      padding: "4px 8px",
                      lineHeight: 1,
                    }}
                  >
                    ‹
                  </button>
                  <span
                    onClick={() => setShowMonthPicker((p) => !p)}
                    style={{
                      fontSize: "1.05rem",
                      fontWeight: "400",
                      color: "#ffffff",
                      letterSpacing: "0.3px",
                      minWidth: "140px",
                      textAlign: "center",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    {calendarView.toLocaleDateString("en-US", {
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                  <button
                    onClick={() => changeCalendarMonth(1)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#ffffff",
                      fontSize: "1.55rem",
                      fontWeight: "300",
                      padding: "4px 8px",
                      lineHeight: 1,
                    }}
                  >
                    ›
                  </button>
                </div>
              </div>
              {showMonthPicker && (
                <div
                  style={{
                    marginBottom: "16px",
                    padding: "16px",
                    background: "rgba(255, 255, 255, 0.25)",
                    borderRadius: "14px",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                  }}
                >
                  {/* Year selector */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "16px",
                      marginBottom: "14px",
                    }}
                  >
                    <button
                      onClick={() => {
                        const d = new Date(calendarView);
                        d.setFullYear(d.getFullYear() - 1);
                        setCalendarView(d);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#ffffff",
                        fontSize: "1.1rem",
                        padding: "4px 8px",
                      }}
                    >
                      ‹
                    </button>
                    <span
                      style={{
                        fontSize: "1rem",
                        fontWeight: "500",
                        color: "#ffffff",
                        minWidth: "60px",
                        textAlign: "center",
                      }}
                    >
                      {calendarView.getFullYear()}
                    </span>
                    <button
                      onClick={() => {
                        const d = new Date(calendarView);
                        d.setFullYear(d.getFullYear() + 1);
                        setCalendarView(d);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#ffffff",
                        fontSize: "1.1rem",
                        padding: "4px 8px",
                      }}
                    >
                      ›
                    </button>
                  </div>
                  {/* Month grid */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4,1fr)",
                      gap: "8px",
                    }}
                  >
                    {[
                      "Jan",
                      "Feb",
                      "Mar",
                      "Apr",
                      "May",
                      "Jun",
                      "Jul",
                      "Aug",
                      "Sep",
                      "Oct",
                      "Nov",
                      "Dec",
                    ].map((m, i) => {
                      const isSelected = calendarView.getMonth() === i;
                      return (
                        <button
                          key={m}
                          onClick={() => {
                            const d = new Date(calendarView);
                            d.setMonth(i);
                            setCalendarView(d);
                            setShowMonthPicker(false);
                          }}
                          style={{
                            padding: "8px 4px",
                            borderRadius: "8px",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "0.82rem",
                            fontWeight: isSelected ? "500" : "400",
                            background: isSelected
                              ? "linear-gradient(135deg,#86f7dc 0%,#a9fbe9 100%)"
                              : "rgba(255, 255, 255, 0.09)",
                            color: isSelected ? "#06301f" : "rgba(236, 228, 255, 0.82)",
                            transition: "all 0.15s",
                          }}
                        >
                          {m}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7,1fr)",
                  gap: "6px",
                }}
              >
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                  <div
                    key={d}
                    style={{
                      textAlign: "center",
                      padding: "8px",
                      fontSize: "0.68rem",
                      fontWeight: "400",
                      color: "rgba(208, 198, 244, 0.62)",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {d}
                  </div>
                ))}
                {(() => {
                  const { daysInMonth, startingDayOfWeek, year, month } =
                    getDaysInMonth(calendarView);
                  const cells = [];
                  for (let i = 0; i < startingDayOfWeek; i++)
                    cells.push(
                      <div key={"e" + i} style={{ padding: "8px" }} />
                    );
                  for (let day = 1; day <= daysInMonth; day++) {
                    const date = new Date(year, month, day),
                      hasEntry = hasEntryOnDate(date),
                      isToday =
                        date.toDateString() === new Date().toDateString(),
                      isSelected =
                        selectedCalendarDate &&
                        date.toDateString() ===
                          selectedCalendarDate.toDateString();
                    cells.push(
                      <div
                        key={day}
                        onClick={() => {
                          if (hasEntry) {
                            setSelectedCalendarDate(isSelected ? null : date);
                            if (!isSelected) setHistoryView("all");
                          }
                        }}
                        style={{
                          padding: "10px",
                          textAlign: "center",
                          borderRadius: "10px",
                          background: isSelected
                            ? "linear-gradient(135deg,#86f7dc 0%,#a9fbe9 100%)"
                            : hasEntry
                            ? "rgba(255, 255, 255, 0.3)"
                            : isToday
                            ? "rgba(150, 150, 168, 0.14)"
                            : "transparent",
                          color: isSelected
                            ? "#06301f"
                            : hasEntry
                            ? "#ffffff"
                            : isToday
                              ? "rgba(236, 228, 255, 0.82)"
                              : "rgba(255, 255, 255, 0.86)",
                          fontWeight:
                            isSelected || hasEntry || isToday ? "500" : "400",
                          fontSize: "0.85rem",
                          cursor: hasEntry ? "pointer" : "default",
                          border: "none",
                          transition: "all 0.2s ease",
                        }}
                        title={hasEntry ? "Entry made on this day" : ""}
                      >
                        {day}
                      </div>
                    );
                  }
                  return cells;
                })()}
              </div>
              <div
                style={{
                  marginTop: "16px",
                  display: "flex",
                  gap: "16px",
                  fontSize: "0.7rem",
                  color: "rgba(208, 198, 244, 0.62)",
                  flexWrap: "wrap",
                }}
              >
                {[
                  ["rgba(255, 255, 255, 0.3)", "Entry made"],
                  ["rgba(150, 150, 168, 0.14)", "Today"],
                  ["linear-gradient(135deg,#86f7dc 0%,#a9fbe9 100%)", "Selected"],
                ].map(([bg, label]) => (
                  <div
                    key={label}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <div
                      style={{
                        width: "14px",
                        height: "14px",
                        borderRadius: "4px",
                        background: bg,
                        border: "none",
                      }}
                    />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Entry history */}
            <div style={pnl}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <h3
                  style={{
                    fontSize: "1.05rem",
                    fontWeight: "400",
                    color: "#ffffff",
                    letterSpacing: "0.3px",
                    margin: 0,
                  }}
                >
                  Entry History
                </h3>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {selectedCalendarDate && (
                    <button
                      onClick={() => setSelectedCalendarDate(null)}
                      style={{
                        padding: "6px 14px",
                        fontSize: "0.8rem",
                        border: "1px solid rgba(255, 255, 255, 0.16)",
                        borderRadius: "8px",
                        background: "rgba(255, 255, 255, 0.15)",
                        color: "rgba(236, 228, 255, 0.82)",
                        cursor: "pointer",
                      }}
                    >
                      Clear filter
                    </button>
                  )}
                  {["day", "month", "year", "all"].map((v) => (
                    <button
                      key={v}
                      onClick={() => {
                        setHistoryView(v);
                        setSelectedCalendarDate(null);
                      }}
                      style={timeBtn(
                        historyView === v && !selectedCalendarDate
                      )}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>

              {filteredHistoryTable.length > 0 ? (
                <div>
                  {[
                    {
                      key: "usdc",
                      label: "USDC",
                      color: "#ffffff",
                      unit: "USDC",
                    },
                    {
                      key: "sgdCoin",
                      label: "SGD (crypto)",
                      color: "#ffffff",
                      unit: "SGD",
                    },
                    { key: "eth", label: "ETH", color: "#ffffff", unit: "ETH" },
                    { key: "sol", label: "SOL", color: "#ffffff", unit: "SOL" },
                    { key: "bnb", label: "BNB", color: "#ffffff", unit: "BNB" },
                    {
                      key: "cash",
                      label: "Cash",
                      color: "#ffffff",
                      unit: "SGD $",
                    },
                    {
                      key: "primeXBT",
                      label: "PrimeXBT",
                      color: "#ffffff",
                      unit: "SGD $",
                    },
                    {
                      key: "posb",
                      label: "POSB",
                      color: "#ffffff",
                      unit: "SGD $",
                    },
                    {
                      key: "ocbc",
                      label: "OCBC",
                      color: "#ffffff",
                      unit: "SGD $",
                    },
                  ].map(({ key, label, color, unit }) => {
                    const rows = filteredHistoryTable.filter(
                      (e) => e[key] !== null && e[key] !== undefined
                    );
                    if (rows.length === 0) return null;
                    const isCrypto = [
                      "usdc",
                      "sgdCoin",
                      "eth",
                      "sol",
                      "bnb",
                    ].includes(key);
                    const fmt = (v) =>
                      isCrypto
                        ? parseFloat(v || 0).toFixed(2) + " " + unit
                        : "SGD $" + parseFloat(v || 0).toFixed(2);
                    const fmtChange = (v) => parseFloat(v).toFixed(2);
                    return (
                      <div key={key} style={{ marginBottom: "24px" }}>
                        <h4
                          style={{
                            fontSize: "0.88rem",
                            fontWeight: "400",
                            color,
                            marginBottom: "10px",
                            letterSpacing: "0.3px",
                          }}
                        >
                          {label}
                        </h4>
                        {rows.map((entry, idx, arr) => {
                          const d = new Date(entry.date);
                          const prev = arr[idx + 1]?.[key];
                          const change =
                            prev !== undefined && prev !== null
                              ? entry[key] - prev
                              : null;
                          return (
                            <div
                              key={entry.date}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "11px 14px",
                                background: "rgba(255, 255, 255, 0.09)",
                                borderRadius: "10px",
                                marginBottom: "8px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "0.8rem",
                                    color: "#ffffff",
                                  }}
                                >
                                  {d.toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "2-digit",
                                  })}{" "}
                                  {d.toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </span>
                                {change !== null && (
                                  <span
                                    style={{
                                      fontSize: "0.68rem",
                                      color:
                                        change >= 0 ? "#86f7dc" : "#ffb4c8",
                                      marginTop: "2px",
                                    }}
                                  >
                                    {change >= 0 ? "+" : ""}
                                    {fmtChange(change)}
                                  </span>
                                )}
                              </div>
                              <span
                                style={{
                                  fontSize: "0.85rem",
                                  fontWeight: "400",
                                  color,
                                }}
                              >
                                {fmt(entry[key])}
                              </span>
                              <button
                                onClick={() => deleteEntry(entry.date)}
                                title="Delete entry"
                                aria-label="Delete entry"
                                style={{
                                  width: "22px",
                                  height: "22px",
                                  borderRadius: "5px",
                                  background: "rgba(255, 180, 200, 0.15)",
                                  border: "1px solid rgba(255, 180, 200, 0.2)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  fontSize: "0.65rem",
                                  color: "#ffb4c8",
                                  flexShrink: 0,
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p
                  style={{
                    textAlign: "center",
                    color: "rgba(208, 198, 244, 0.62)",
                    padding: "40px 0",
                    fontSize: "0.9rem",
                  }}
                >
                  {history.length === 0
                    ? "No entry history yet"
                    : selectedCalendarDate
                    ? "No entries for selected date"
                    : "No entries for selected timeframe"}
                </p>
              )}
            </div>
            {/* Portfolio value line chart */}
            <div style={{ ...pnl, marginBottom: "14px", marginTop: "14px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <h3
                  style={{
                    fontSize: "1.05rem",
                    fontWeight: "400",
                    color: "#ffffff",
                    letterSpacing: "0.3px",
                    margin: 0,
                  }}
                >
                  Portfolio Value
                </h3>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {["day", "month", "year", "all"].map((tf) => (
                    <button
                      key={tf}
                      onClick={() => setTimeframe(tf)}
                      style={timeBtn(timeframe === tf)}
                    >
                      {tf}
                    </button>
                  ))}
                </div>
              </div>
              {filteredHistory.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart
                    data={filteredHistory.filter((e) => e.total != null)}
                    margin={{ top: 5, right: 20, left: 10, bottom: 20 }}
                  >
                    <defs>
                      <linearGradient id="gTotal" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="5%"
                          stopColor="#c4b0ff"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#c4b0ff"
                          stopOpacity={0}
                        />
                      </linearGradient>
                      <linearGradient id="gCrypto" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="5%"
                          stopColor="#86f7dc"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#86f7dc"
                          stopOpacity={0}
                        />
                      </linearGradient>
                      <linearGradient id="gFiat" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="5%"
                          stopColor="#c4b0ff"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#c4b0ff"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="rgba(255, 255, 255, 0.1)"
                    />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatXAxisLabel}
                      stroke="rgba(208, 198, 244, 0.62)"
                      angle={-45}
                      textAnchor="end"
                      height={90}
                      style={{ fontSize: "0.75rem" }}
                    />
                    <YAxis stroke="rgba(208, 198, 244, 0.62)" style={{ fontSize: "0.75rem" }} />
                    <Tooltip
                      labelFormatter={formatTooltipLabel}
                      formatter={formatValue}
                      contentStyle={{
                        color: "#ffffff",
                        background: "rgba(44, 30, 116, 0.95)",
                        border: "1px solid rgba(255, 255, 255, 0.16)",
                        borderRadius: "8px",
                        fontSize: "0.8rem",
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="total"
                      stroke="#c4b0ff"
                      strokeWidth={2}
                      fill="url(#gTotal)"
                      name="Total"
                      dot={false}
                      connectNulls
                    />
                    <Area
                      type="monotone"
                      dataKey="crypto"
                      stroke="#86f7dc"
                      strokeWidth={2}
                      fill="url(#gCrypto)"
                      name="Crypto"
                      dot={false}
                      connectNulls
                    />
                    <Area
                      type="monotone"
                      dataKey="fiat"
                      stroke="#c4b0ff"
                      strokeWidth={2}
                      fill="url(#gFiat)"
                      name="Fiat"
                      dot={false}
                      connectNulls
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <p
                  style={{
                    textAlign: "center",
                    color: "rgba(208, 198, 244, 0.62)",
                    padding: "80px 0",
                    fontSize: "0.9rem",
                  }}
                >
                  {history.length === 0
                    ? "Edit holdings to start tracking"
                    : "No data for selected timeframe"}
                </p>
              )}
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════════════════
            TAB: INFINITY  ♾️
        ════════════════════════════════════════════════════════ */}
        {activeTab === "infinity" && (
          <div style={pnl}>
            {/* Quote field — same design as tabs 1 & 2, separate quote */}
            <div style={{ marginBottom: "20px" }}>
              {!isEditingInfinityQuote ? (
                <div
                  onClick={() => setIsEditingInfinityQuote(true)}
                  style={{
                    padding: "18px 32px",
                    cursor: "pointer",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%,-50%)",
                      width: "90%",
                      height: "95%",
                      background:
                        "radial-gradient(ellipse at center,rgba(255, 255, 255, 0.09) 0%,rgba(255, 255, 255, 0.2) 40%,transparent 70%)",
                      filter: "blur(20px)",
                      zIndex: 0,
                      pointerEvents: "none",
                      opacity: 0.25,
                    }}
                  />
                  <p
                    style={{
                      fontSize: "1.02rem",
                      color: "#ffffff",
                      fontStyle: "italic",
                      textAlign: "center",
                      margin: 0,
                      lineHeight: "1.7",
                      fontWeight: "400",
                      textShadow: "0 0 12px rgba(255, 255, 255, 0.3)",
                      position: "relative",
                      zIndex: 1,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {'"' + (infinityQuote || "").trimEnd() + '"'}
                  </p>
                </div>
              ) : (
                <div>
                  <textarea
                    value={infinityQuote}
                    onChange={(e) => setInfinityQuote(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "16px",
                      background: "rgba(14, 8, 60, 0.34)",
                      border: "1px solid rgba(255, 255, 255, 0.16)",
                      borderRadius: "12px",
                      fontSize: "1rem",
                      color: "rgba(236, 228, 255, 0.82)",
                      fontStyle: "italic",
                      resize: "vertical",
                      minHeight: "80px",
                      fontFamily: "inherit",
                      boxSizing: "border-box",
                      whiteSpace: "pre-wrap",
                    }}
                    placeholder="Enter your quote..."
                  />
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      marginTop: "10px",
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      onClick={() => {
                        setInfinityQuote(
                          localStorage.getItem("infinity-quote") ||
                            "Create from the infinite field."
                        );
                        setIsEditingInfinityQuote(false);
                      }}
                      style={{
                        padding: "8px 16px",
                        background: "rgba(255, 255, 255, 0.1)",
                        color: "rgba(236, 228, 255, 0.82)",
                        border: "1px solid rgba(255, 255, 255, 0.16)",
                        borderRadius: "9px",
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        fontWeight: "500",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        localStorage.setItem("infinity-quote", infinityQuote);
                        setIsEditingInfinityQuote(false);
                      }}
                      style={{
                        padding: "8px 16px",
                        background:
                          "linear-gradient(135deg,#86f7dc 0%,#a9fbe9 100%)",
                        color: "#06301f",
                        border: "none",
                        borderRadius: "9px",
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        fontWeight: "500",
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Geometry container: Metatron SVG (bg) + Three.js Merkaba (fg) */}
            <div
              style={{
                position: "relative",
                width: "100%",
                paddingTop: "100%",
                marginBottom: "24px",
                overflow: "visible",
              }}
            >
              {/* Metatron's Cube — correct circle sizes (adjacent circles just touch), thick glowing lines */}
              <svg
                className="metatron-pulse"
                style={{
                  position: "absolute",
                  inset: "-5%",
                  width: "110%",
                  height: "110%",
                  overflow: "visible",
                }}
                viewBox="-20 -20 440 440"
                preserveAspectRatio="xMidYMid meet"
              >
                <defs>
                  <filter id="mg" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="2.5" result="b" />
                    <feMerge>
                      <feMergeNode in="b" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                  <filter id="mg2" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="4" result="b" />
                    <feMerge>
                      <feMergeNode in="b" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                {(() => {
                  const cx = 200,
                    cy = 200;
                  const r1 = 70; // inner hexagon ring radius
                  const r2 = 140; // outer hexagon ring radius
                  // Circle radius = r1/2 = 35, so adjacent inner circles just touch
                  // (inner hex side length = r1 = 70, so distance between adjacent = 70, circle r = 35 → they touch)
                  const circR = 35;
                  const angles = [0, 1, 2, 3, 4, 5].map(
                    (i) => ((-90 + i * 60) * Math.PI) / 180
                  );

                  const nodes = [
                    [cx, cy],
                    ...angles.map((a) => [
                      cx + r1 * Math.cos(a),
                      cy + r1 * Math.sin(a),
                    ]),
                    ...angles.map((a) => [
                      cx + r2 * Math.cos(a),
                      cy + r2 * Math.sin(a),
                    ]),
                  ];

                  const els = [];

                  // All 78 connecting lines
                  for (let i = 0; i < nodes.length; i++) {
                    for (let j = i + 1; j < nodes.length; j++) {
                      els.push(
                        <line
                          key={"l" + i + "-" + j}
                          x1={nodes[i][0]}
                          y1={nodes[i][1]}
                          x2={nodes[j][0]}
                          y2={nodes[j][1]}
                          stroke="rgba(255, 255, 255, 0.4)"
                          strokeWidth="1.4"
                          filter="url(#mg)"
                        />
                      );
                    }
                  }

                  // 13 circles at each node
                  nodes.forEach(([nx, ny], i) => {
                    els.push(
                      <circle
                        key={"c" + i}
                        cx={nx}
                        cy={ny}
                        r={circR}
                        stroke="rgba(255, 255, 255, 0.45)"
                        strokeWidth="1.5"
                        fill="none"
                        filter="url(#mg)"
                      />
                    );
                  });

                  // Vertex dots at outer hexagon nodes only (indices 7-12)
                  nodes.forEach(([nx, ny], i) => {
                    if (i >= 7) {
                      els.push(
                        <circle
                          key={"dot" + i}
                          cx={nx}
                          cy={ny}
                          r={3}
                          fill="rgba(255, 255, 255, 0.95)"
                        />
                      );
                    }
                  });

                  return els;
                })()}
              </svg>

              {/* Separate SVG layer for hexagon outlines — rendered on top, crisp and visible */}
              <svg
                className="metatron-pulse"
                style={{
                  position: "absolute",
                  inset: "-5%",
                  width: "110%",
                  height: "110%",
                  overflow: "visible",
                  pointerEvents: "none",
                }}
                viewBox="-20 -20 440 440"
                preserveAspectRatio="xMidYMid meet"
              >
                {(() => {
                  const cx = 200,
                    cy = 200,
                    r1 = 70,
                    r2 = 140;
                  const angles = [0, 1, 2, 3, 4, 5].map(
                    (i) => ((-90 + i * 60) * Math.PI) / 180
                  );
                  const inner = angles.map((a) => [
                    cx + r1 * Math.cos(a),
                    cy + r1 * Math.sin(a),
                  ]);
                  const outer = angles.map((a) => [
                    cx + r2 * Math.cos(a),
                    cy + r2 * Math.sin(a),
                  ]);
                  const hexLines = [];
                  // All 6 outer hexagon edges
                  for (let i = 0; i < 6; i++) {
                    const j = (i + 1) % 6;
                    hexLines.push(
                      <line
                        key={"oh" + i}
                        x1={outer[i][0]}
                        y1={outer[i][1]}
                        x2={outer[j][0]}
                        y2={outer[j][1]}
                        stroke="rgba(255, 255, 255, 0.52)"
                        strokeWidth="1.5"
                      />
                    );
                  }
                  // All 6 inner hexagon edges
                  for (let i = 0; i < 6; i++) {
                    const j = (i + 1) % 6;
                    hexLines.push(
                      <line
                        key={"ih" + i}
                        x1={inner[i][0]}
                        y1={inner[i][1]}
                        x2={inner[j][0]}
                        y2={inner[j][1]}
                        stroke="rgba(255, 255, 255, 0.48)"
                        strokeWidth="1.4"
                      />
                    );
                  }
                  // Horizontal cross-connections (diagonals that appear as horizontal lines in the reference)
                  // Outer: top-left(5) ↔ top-right(1), bot-left(4) ↔ bot-right(2)
                  // Inner: top-left(5) ↔ top-right(1), bot-left(4) ↔ bot-right(2)
                  hexLines.push(
                    <line
                      key="oxh1"
                      x1={outer[5][0]}
                      y1={outer[5][1]}
                      x2={outer[1][0]}
                      y2={outer[1][1]}
                      stroke="rgba(255, 255, 255, 0.52)"
                      strokeWidth="1.5"
                    />,
                    <line
                      key="oxh2"
                      x1={outer[4][0]}
                      y1={outer[4][1]}
                      x2={outer[2][0]}
                      y2={outer[2][1]}
                      stroke="rgba(255, 255, 255, 0.52)"
                      strokeWidth="1.5"
                    />,
                    <line
                      key="ixh1"
                      x1={inner[5][0]}
                      y1={inner[5][1]}
                      x2={inner[1][0]}
                      y2={inner[1][1]}
                      stroke="rgba(255, 255, 255, 0.48)"
                      strokeWidth="1.4"
                    />,
                    <line
                      key="ixh2"
                      x1={inner[4][0]}
                      y1={inner[4][1]}
                      x2={inner[2][0]}
                      y2={inner[2][1]}
                      stroke="rgba(255, 255, 255, 0.48)"
                      strokeWidth="1.4"
                    />
                  );
                  // Vertical center line: top outer node (0) to bottom outer node (3)
                  hexLines.push(
                    <line
                      key="vcenter"
                      x1={outer[0][0]}
                      y1={outer[0][1]}
                      x2={outer[3][0]}
                      y2={outer[3][1]}
                      stroke="rgba(255, 255, 255, 0.52)"
                      strokeWidth="1.5"
                    />
                  );
                  return hexLines;
                })()}
              </svg>

              {/* Three.js canvas — transparent bg, sits over Metatron SVG */}
              <canvas
                ref={merkabaCRef}
                className="merkaba-canvas"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  background: "transparent",
                }}
              />
            </div>

            {/* Rotation speed slider — identical to Manifestation Multiplier */}
            <div
              style={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "6px",
              }}
            >
              {/* Glow blob behind slider */}
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%,-50%)",
                  width: "60%",
                  height: "60px",
                  background:
                    "radial-gradient(ellipse at center, rgba(255, 255, 255, 0.16) 0%, transparent 70%)",
                  filter: "blur(12px)",
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  width: "100%",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {/* Left: blank space */}
                <div style={{ width: "28px", flexShrink: 0 }} />
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={infinitySpinSpeed}
                  onChange={(e) =>
                    setInfinitySpinSpeed(parseInt(e.target.value))
                  }
                  className="infinity-slider"
                  style={{
                    flex: 1,
                    outline: "none",
                    border: "none",
                    boxShadow: "none",
                  }}
                />
                {/* Right: infinity icon */}
                <div
                  style={{
                    width: "28px",
                    flexShrink: 0,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <svg
                    width="24"
                    height="16"
                    viewBox="0 0 26 18"
                    fill="none"
                    style={{
                      filter:
                        "drop-shadow(0 0 4px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 10px rgba(236, 228, 255, 0.5))",
                    }}
                  >
                    <path
                      d="M13 9 C13 9 10 4 6.5 4 C3.5 4 1 6.5 1 9 C1 11.5 3.5 14 6.5 14 C10 14 13 9 13 9 C13 9 16 4 19.5 4 C22.5 4 25 6.5 25 9 C25 11.5 22.5 14 19.5 14 C16 14 13 9 13 9 Z"
                      stroke="rgba(255, 255, 255, 0.9)"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Export / Import + Change Key — always at bottom, barely visible ── */}
        <div
          style={{
            marginTop: "32px",
            display: "flex",
            justifyContent: "center",
            gap: "12px",
            opacity: 0.35,
            transition: "opacity 0.3s",
          }}
          onMouseOver={(e) => (e.currentTarget.style.opacity = "1")}
          onMouseOut={(e) => (e.currentTarget.style.opacity = "0.35")}
        >
          <button
            onClick={exportData}
            style={{
              padding: "7px 14px",
              background: "rgba(134, 247, 220, 0.15)",
              color: "#ffffff",
              borderRadius: "8px",
              border: "1px solid rgba(134, 247, 220, 0.2)",
              cursor: "pointer",
              fontSize: "0.72rem",
              letterSpacing: "0.3px",
            }}
          >
            Export Data
          </button>
          <label
            style={{
              padding: "7px 14px",
              background: "rgba(134, 247, 220, 0.15)",
              color: "#ffffff",
              borderRadius: "8px",
              border: "1px solid rgba(134, 247, 220, 0.2)",
              cursor: "pointer",
              fontSize: "0.72rem",
              letterSpacing: "0.3px",
              display: "inline-block",
            }}
          >
            Import Data
            <input
              type="file"
              accept=".json"
              onChange={importData}
              style={{ display: "none" }}
            />
          </label>
          <button
            onClick={() => setShowApiInput(true)}
            style={{
              padding: "7px 14px",
              background: "rgba(255, 255, 255, 0.1)",
              color: "#ffffff",
              borderRadius: "8px",
              border: "1px solid rgba(255, 255, 255, 0.16)",
              cursor: "pointer",
              fontSize: "0.72rem",
              letterSpacing: "0.3px",
            }}
          >
            Change Key
          </button>
        </div>
      </div>
    </div>
  );
}
